/**
 * 6 关中/大 Boss 定义工厂 + 内容层共享脚本工具。
 * stage 模块从这里 import 小兵脚本工具（aimOf/fireTimer/弹行为）与 boss 工厂。
 *
 * bossDef 结构（交给场景层构造 game/enemies.js 的 Boss）：
 *   { name, title, spriteKey, x, y, phases }
 *   phases: [{ name, spellCard, hp, timeLimit, enter(boss), update(boss, dt, ctx) }]
 *   符卡阶段另带 desc：该符的介绍文（boss 自述口吻），符卡练习界面展示，
 *   经 L() 走 CONTENT 表多语（改后必跑 tools/i18n-check.mjs）。
 *   非符/符卡严格交替，第一段必为非符。
 *   hp 预算规则：hp ≈ timeLimit × 20（8 角色最低火力有效 DPS ≥ 23，见 characters.js
 *   机炮保底设计），保证最低火力也能在时限内打空每张符卡；明显偏离会让
 *   阶段退化为纯超时转段（stage-smoke 校验比率 12-30）。
 *   慢成型弹幕约束：周期型弹幕满火力击杀时间 ≥ 2.5 完整周期（满火力 DPS 按 80 计）；
 *   多幕/长叙事结构用 HP 分段门控而非时间轴（CAVE 阈值模型，任何 DPS 下每幕必出场）。
 *   移动脚本全部写在 enter/update 里：enter 定点 → update 漂移 + 缓慢游移。
 * node 可直接 import。
 */
import { TAU, randRange } from '../../engine/math.js';
import { gatedInterval } from '../../game/modsGate.js';
import * as P from '../../game/patterns.js';
import { VIEW_W } from '../../config.js';

// ---------------------------------------------------------------- 共享工具

/**
 * 自机狙角度。兼容场景层三种 ctx 形态：
 * ctx.aim(x,y) 函数 / ctx.player{x,y} / ctx.playerX,playerY。
 */
export function aimOf(ctx, x, y) {
  if (ctx) {
    if (typeof ctx.aim === 'function') return ctx.aim(x, y);
    if (ctx.player) return Math.atan2(ctx.player.y - y, ctx.player.x - x);
    if (ctx.playerX !== undefined) return Math.atan2(ctx.playerY - y, ctx.playerX - x);
  }
  return Math.PI / 2;
}

/**
 * 间隔计时器：每 interval 秒返回一次 true。
 * key 用于同一实体上挂多个计时器。
 * （难度差异不走射速乘率——由 patterns.js 的弹量结构分支承载，见该文件头部）
 */
export function fireTimer(e, dt, interval, key = '_ft') {
  // 槽位被非数字（如 burstTimer 状态对象）污染时自愈，避免静默永久停火
  e[key] = (typeof e[key] === 'number' ? e[key] : 0) + dt;
  // 全局门控（game/modsGate.js）：困困·梦境领域停火 / 惑惑·混乱力场射速减半
  const iv = gatedInterval(interval);
  if (e[key] >= iv) {
    e[key] -= iv;
    return true;
  }
  return false;
}

/**
 * 连发-停顿节律：连发 n 次（间隔 interval 秒）后停顿 pause 秒，循环。
 * 与 fireTimer 一样按 key 在实体上挂状态，返回 true 的帧触发一次发射。
 */
export function burstTimer(e, dt, interval, n, pause, key = '_bt') {
  // 与 fireTimer 的数字槽位隔离：键默认 '_bt'；槽位被旧值（数字）占用时重置
  const s = e[key] && typeof e[key] === 'object' ? e[key] : (e[key] = { c: 0, t: 0 });
  s.t += dt;
  if (s.t < (s.c >= n ? pause : interval)) return false;
  s.t = 0;
  s.c = s.c >= n ? 1 : s.c + 1;
  return true;
}

/** 正弦扫频：在 [min,max] 间以 period 秒为周期往返（参数呼吸）。 */
export function osc(t, period, min, max) {
  return min + (max - min) * (0.5 + 0.5 * Math.sin((t / period) * TAU));
}

/** 三角扫频：min→max→min 线性往返（period 秒一个来回）。 */
export function tri(t, period, min, max) {
  const k = (t / period) % 1;
  return min + (max - min) * (k < 0.5 ? k * 2 : 2 - k * 2);
}

/** 时间序列取值：每 step 秒循环取数组下一项（颜色/缺口/档位轮换）。 */
export function seq(t, step, values) {
  return values[Math.floor(t / step) % values.length];
}

/** 方向振荡：每 period 秒反转一次，返回 ±1（spin/卷曲方向换向）。 */
export function flip(t, period) {
  return Math.floor(t / period) % 2 === 0 ? 1 : -1;
}

/** 来回扫场角：以 speed rad/s 旋转、每 period 秒往返一次的连续角（三角波，无状态）。 */
export function sweepAngle(t, speed, period) {
  return speed * tri(t, period * 2, 0, period);
}

/**
 * Boss 移动脚本：向 (tx,ty) 漂移，到位后以 phaseTime 做缓慢正弦游移。
 * ampX/ampY 为游移幅度，wob 为游移角速度。
 * 游移幅度在到位后 ~1.2s 内渐入（避免切入瞬间位置跳变）。
 */
export function bossMove(boss, dt, tx, ty, speed = 90, ampX = 0, ampY = 0, wob = 0.7) {
  // 目标点被脚本改到新位置（enterAt/中途换位）时脱离游移，重新走移动分支
  if (boss._atAnchor && Math.hypot(tx - boss._anchorX, ty - boss._anchorY) > 2) {
    boss._atAnchor = false;
    boss._wanderT = 0;
  }
  // 已到锚点：进入游移。游移以锚点为基准写偏移，移动分支不再回拉
  // （旧实现把偏移判为"未到位"回拉，±70px 游移被掐死成 ±2px 抖动——历史 bug）。
  if (boss._atAnchor) {
    if (ampX || ampY) {
      boss._wanderT = Math.min(1, (boss._wanderT || 0) + dt / 1.2);
      const k = boss._wanderT;
      boss.x = tx + Math.sin(boss.phaseTime * wob) * ampX * k;
      boss.y = ty + Math.sin(boss.phaseTime * wob * 0.6 + 1) * ampY * k;
    } else {
      boss.x = tx;
      boss.y = ty;
    }
    return;
  }
  const dx = tx - boss.x;
  const dy = ty - boss.y;
  const d = Math.hypot(dx, dy);
  if (d > 2) {
    const step = Math.min(d, speed * dt);
    boss.x += (dx / d) * step;
    boss.y += (dy / d) * step;
    boss._wanderT = 0; // 移动中不游摆
  } else {
    boss._atAnchor = true;
    boss._anchorX = tx;
    boss._anchorY = ty;
    boss.x = tx;
    boss.y = ty;
  }
}

/** enter 工厂：记录定点目标。 */
export function enterAt(tx, ty) {
  return (boss) => {
    boss._tx = tx;
    boss._ty = ty;
  };
}

// ---------------------------------------------------- 自定义弹行为（onUpdate）

/** 曲线弹：速度方向每帧偏转 rate rad/s，dur 秒内衰减到 0（之后直线飞走，防滞留撑爆弹量）。 */
export function curveBullet(rate, dur = 3) {
  return (b, dt) => {
    const k = Math.max(0, 1 - b.age / dur);
    const a = Math.atan2(b.vy, b.vx) + rate * k * dt;
    const s = Math.hypot(b.vx, b.vy);
    b.vx = Math.cos(a) * s;
    b.vy = Math.sin(a) * s;
  };
}

/** 滞留孢子弹：~1.2s 内减速到悬停，hold 秒后沿原方向散射 + 缓慢下沉（让开通道）。 */
export function sporeBullet(hold = 1.6, fallSpeed = 1.0) {
  return (b, dt) => {
    if (b.age <= dt) {
      // 出生第一帧捕获原方向（age<=dt 等价于首帧，池复用安全——
      // 旧 `b._sdx === undefined` 懒判断会让回收弹沿用上一世的方向）
      const s = Math.hypot(b.vx, b.vy);
      b._sdx = s > 0 ? b.vx / s : 0;
      b._sdy = s > 0 ? b.vy / s : 0;
    }
    if (b.age < 1.2) {
      const k = Math.max(0, 1 - 2.2 * dt);
      b.vx *= k;
      b.vy *= k;
    } else if (b.age > 1.2 + hold) {
      // 散射：沿发射方向加速（环扩张、缝隙变大），并缓慢下沉
      b.vx += (b._sdx || 0) * 1.4 * dt;
      b.vy += (b._sdy || 0) * 1.4 * dt + 0.4 * dt;
      const sp = Math.hypot(b.vx, b.vy);
      const max = Math.max(1.3, fallSpeed);
      if (sp > max) {
        b.vx *= max / sp;
        b.vy *= max / sp;
      }
    }
  };
}

/** 加速弹：速率按 rate/s 指数增长（S5 特色）。 */
export function accelBullet(rate = 0.8) {
  return (b, dt) => {
    const k = 1 + rate * dt;
    b.vx *= k;
    b.vy *= k;
    // 速度上限 ~6px/帧：指数加速无上限时单帧步长可超过命中重叠窗口，
    // 离散圆判定会整帧跳过擦边命中（历史 bug：判定不可靠）
    const sp = Math.hypot(b.vx, b.vy);
    if (sp > 6) {
      b.vx *= 6 / sp;
      b.vy *= 6 / sp;
    }
  };
}

/** 定时链弹：delay 秒内减速悬停，到点沿 (ang, spd) 突进（S3 特色）。
 *  age 窗口无状态触发（prevAge < delay <= age 那一帧突进），池复用安全。 */
export function chainBullet(delay, ang, spd) {
  return (b, dt) => {
    if (b.age < delay) {
      b.vx *= 0.9;
      b.vy *= 0.9;
    } else if (b.age - dt < delay) {
      b.vx = Math.cos(ang) * spd;
      b.vy = Math.sin(ang) * spd;
    }
  };
}

/** 蛇形弹：速度方向按正弦来回摆动（S6 弹幕变换）。 */
export function zigzagBullet(amp = 2.2, freq = 4) {
  return (b, dt) => {
    const a = Math.atan2(b.vy, b.vx) + Math.sin(b.age * freq) * amp * dt;
    const s = Math.hypot(b.vx, b.vy);
    b.vx = Math.cos(a) * s;
    b.vy = Math.sin(a) * s;
  };
}

/**
 * 折线弹：atAge 秒时速度方向瞬时偏转 turn 弧度（滑轨换轨感，S3 机械）。
 * alternate 为 true 时按弹 id 奇偶交替 ±turn（多弹齐射铺出 ┐┌ 交错轨道）。
 * 无状态：只在 age 跨过 atAge 的那一帧触发一次（prevAge < atAge <= age），池复用安全。
 */
export function kinkBullet(atAge, turn, alternate = false) {
  return (b, dt) => {
    if (b.age >= atAge && b.age - dt < atAge) {
      const t = alternate && b.id % 2 === 1 ? -turn : turn;
      const a = Math.atan2(b.vy, b.vx) + t;
      const s = Math.hypot(b.vx, b.vy);
      b.vx = Math.cos(a) * s;
      b.vy = Math.sin(a) * s;
    }
  };
}

/**
 * 风摆弹：保持原弹道，在垂直于速度方向做小幅正弦横漂（振幅小、频率慢）。
 * 与 zigzagBullet（改变速度角度、快而大像蛇）不同——driftBullet 只是"浮着晃"，
 * 弹道去向不变、瞄准关系不破，用于给子弹加"被风托着晃"的性格（Stage 1 天空）。
 * 摆动幅度 ≈ amp*60/freq px（amp 单位 px/帧）。
 */
export function driftBullet(amp = 0.3, freq = 2) {
  return (b, dt) => {
    const a = Math.atan2(b.vy, b.vx) + Math.PI / 2;
    const sway = Math.sin(b.age * freq) * amp;
    b.x += Math.cos(a) * sway * dt * 60;
    b.y += Math.sin(a) * sway * dt * 60;
  };
}

/** 组合多个弹行为为一个 onUpdate（如 曲线+风摆 = 湍流翻滚）。须透传 ctx（shadow/lockon 等依赖）。 */
export function composeBullets(...fns) {
  return (b, dt, ctx) => {
    for (const fn of fns) fn(b, dt, ctx);
  };
}

/**
 * 怕生弹：追踪强度按正弦在 ±homingBase 间振荡（负值=背离自机转向）——
 * 精魄先追 ~1.7s、迟疑退开 ~1.7s、再追，轨迹是螺旋靠近的开瓶钻（S4 森之精魄）。
 * 出生即追（sin 正向起步）；homing 基准取生成时的 b.homingBase（含难度缩放）。
 */
export function timidBullet(freq = 1.8) {
  return (b) => {
    b.homing = b.homingBase * Math.sin(b.age * freq);
  };
}

/**
 * 瞬转再瞄（EoSD flag 128 式离散追踪）：生成后每 period 秒瞬时朝自机重瞄一次、
 * 共 count 次（脱窗直飞；boost 可让每瞄略提速"越盯越紧"）。
 * 与连续 homing 的"缠"区分——追踪谱系里 aimed(0 次)↔snap(N 次)↔连续 的离散档。
 * age 窗口分桶无状态；ctx 需含 playerX/playerY。
 */
export function snapBullet(period = 0.9, count = 3, boost = 1.0) {
  return (b, dt, ctx) => {
    const k = Math.floor(b.age / period);
    if (k > Math.floor((b.age - dt) / period) && k <= count && ctx && ctx.playerX !== undefined) {
      const a = Math.atan2(ctx.playerY - b.y, ctx.playerX - b.x);
      const s = Math.hypot(b.vx, b.vy) * boost;
      b.vx = Math.cos(a) * s;
      b.vy = Math.sin(a) * s;
    }
  };
}

/**
 * 追踪窗口（弹寿机制，th06 ReimuA 模型）：出生后 window 秒内追踪正常生效，
 * 脱窗瞬间 homing 与 homingBase 一并清零、速度 ×boost 沿当前方向加速离场——
 * 弹"下班"飞走而不是消失（绕圈追踪弹发射率 > 出屏率的累积问题由此根治）。
 * 与其他行为用 composeBullets 串联时须放最前：先清基准，后续行为读到 0 自然一起下班。
 */
export function guideBullet(window = 6, boost = 1.35) {
  return (b) => {
    if (b.age > window && (b.homing > 0 || b.homingBase > 0)) {
      b.homing = 0;
      b.homingBase = 0;
      b.vx *= boost;
      b.vy *= boost;
    }
  };
}

/**
 * 凝视锁定弹：悬停 delay 秒（眼睛在看你），然后朝自机**当下位置**突进 spd（S5 凝视）。
 * 与 chainBullet（锁发射时固定角度，机械）区分：这个锁的是目标（活物）。
 * age 窗口无状态触发；ctx 需含 playerX/playerY（缺省时沿原方向突进）。
 */
export function lockonBullet(delay, spd) {
  return (b, dt, ctx) => {
    if (b.age < delay) {
      b.vx *= 0.9;
      b.vy *= 0.9;
    } else if (b.age - dt < delay) {
      let a;
      if (ctx && ctx.playerX !== undefined) {
        a = Math.atan2(ctx.playerY - b.y, ctx.playerX - b.x);
      } else {
        a = Math.atan2(b.vy, b.vx);
      }
      b.vx = Math.cos(a) * spd;
      b.vy = Math.sin(a) * spd;
    }
  };
}

/**
 * 渐隐滴：静止悬留，fade 秒内 alpha 线性淡到 0 并回收（彗尾星尘/余晖）。
 *  field 闭包捕获（release；inkBullet 同款已验证模式）。
 */
export function fadeBullet(field, fade = 0.3) {
  return (b) => {
    b.alpha = Math.max(0, 1 - b.age / fade);
    if (b.age >= fade) field.pool.release(b);
  };
}

/**
 * 彗尾弹：飞行中每 step 秒在身后落下一颗渐隐星尘（fade 秒淡没）——流星/彗星拖尾（S6 星界）。
 * 星尘为纯装饰（harmless，不参与命中/擦弹）：拖尾只管华丽，不当绒毛判负。
 * maxAge：超过该年龄不再滴落（"彗尾燃尽"——长寿弹如追踪弹绕圈不退场时，拖尾必须限寿，
 * 否则滴落速率随弹龄累积拖垮弹池）。
 *  field 闭包捕获；滴落触发用 age 窗口序列（floor(age/step) 跨桶那一帧），无 per-bullet 状态。
 */
export function trailBullet(field, { step = 0.08, fade = 0.3, r = 2, color = 'star_cyan', maxAge = 4 } = {}) {
  return (b, dt) => {
    if (b.age >= maxAge) return;
    const k0 = Math.floor((b.age - dt) / step);
    const k1 = Math.floor(b.age / step);
    if (k1 > k0 && k1 >= 1) {
      field.spawn({ x: b.x, y: b.y, speed: 0, angle: 0, r, color, harmless: true, onUpdate: fadeBullet(field, fade) });
    }
  };
}

/**
 * 暗影弹：alpha 由与自机的距离驱动——远时只剩 floor 的鬼影，进入 range 内逐渐显形到 1
 * （S5 暗界"低可见"：场上鬼影幢幢，逼近才现形）。
 * 与 flickerBullet（时间轴明灭）区分：这是空间轴显形；ctx 需含 playerX/playerY（追踪同款数据源）。
 */
export function shadowBullet(range = 190, floor = 0.15) {
  return (b, dt, ctx) => {
    if (!ctx || ctx.playerX === undefined) return;
    const d = Math.hypot(b.x - ctx.playerX, b.y - ctx.playerY);
    const k = Math.max(0, Math.min(1, 1 - d / range));
    b.alpha = floor + (1 - floor) * k;
  };
}

/**
 * 明灭弹：透明度按正弦在 1..dimAlpha 间脉动（周期 period 秒），暗时追踪力按平方衰减到 0
 * ——灯笼鱼诱灯：亮时招引追踪，灭时只剩隐约鬼火沿直线漂。
 * homing 基准取生成时的 b.homingBase（含难度缩放）。
 * syncTime：给定时以该时间源驱动相位（如 () => boss.phaseTime），全场灯火同相明灭、
 * 灭灯窗口统一可读；缺省用 b.age（逐弹各自计时）。
 * desync 为 true 时按弹 id 错相（装饰光点用，像零星明灭的浮游生物）。
 */
export function flickerBullet(period = 3.2, dimAlpha = 0.15, desync = false, syncTime = null) {
  return (b) => {
    const t = syncTime ? syncTime() : b.age;
    const ph0 = desync ? b.id * 2.399 : 0;
    const ph = (Math.sin((t / period) * TAU + ph0 - Math.PI / 2) + 1) / 2; // 0..1
    b.alpha = dimAlpha + (1 - dimAlpha) * ph;
    b.homing = b.homingBase * ph * ph;
  };
}

/**
 * 墨滴弹：先自由下落 fallT 秒，随后滞涩成一团墨云——急减速、轻微下沉、
 * alpha 降到 murk 的浑浊感，滞留 hold 秒，最后 fade 秒内下沉淡出并回收
 * （淡尽必须 release：否则隐形弹既阴人又撑爆弹池）。
 * field 在生成时以闭包捕获（同 splitBullet；onUpdate 的 ctx 不含 field）。
 * 与 sporeBullet（悬停后散射让路）不同：墨云不散射，原地浑浊滞留再淡散，是"遮挡"。
 */
export function inkBullet(field, { fallT = 1.0, hold = 2.8, fade = 1.2, murk = 0.9 } = {}) {
  return (b, dt) => {
    const t = b.age;
    if (t < fallT) return; // 下落期：保持初速
    if (t < fallT + hold) {
      const k = Math.max(0, 1 - 3 * dt); // 滞涩
      b.vx *= k;
      b.vy *= k;
      b.vy += 0.15 * dt; // 轻微下沉
      b.alpha = murk;
    } else {
      b.alpha = Math.max(0, murk * (1 - (t - fallT - hold) / fade));
      b.vy = Math.max(b.vy, 0.3); // 消散时缓缓下沉
      if (b.alpha <= 0) field.pool.release(b); // 淡尽回收
    }
  };
}

/**
 * 漩涡弹（参数式收缩环）：弹位于以 (cx,cy) 为圆心、半径 r0→r1 收缩的环上，
 * 角速度随收缩加快（排水口手感），dur 秒缩到中心后不再干预——
 * 弹以最后的切向速度自然飞离（"甩出"可读）。
 * 逐帧由目标位置反解速度，运动系统照常积分；轨道完全可预测（不做力场模拟），
 * 无池复用状态（全程只依赖 b.age 与闭包常量）。
 */
export function whirlpoolBullet(cx, cy, { r0 = 200, r1 = 30, dur = 3.2, omega = 2.4, theta0 = 0, bite = 0 } = {}) {
  return (b, dt, ctx) => {
    const k = b.age / dur;
    if (k >= 1) {
      // 噬流（bite>0 时）：每 bite 颗在排干瞬间改咬自机（age 窗口无状态）
      if (bite > 0 && (b.age - dt) / dur < 1 && b.id % bite === 0 && ctx && ctx.playerX !== undefined) {
        const a = Math.atan2(ctx.playerY - b.y, ctx.playerX - b.x);
        const s = Math.hypot(b.vx, b.vy) * 1.25;
        b.vx = Math.cos(a) * s;
        b.vy = Math.sin(a) * s;
      }
      return; // 甩出：保持最后的切向速度直线飞走
    }
    const e = k * k * (3 - 2 * k); // smoothstep：先缓后急的收缩
    const r = r0 + (r1 - r0) * e;
    const th = theta0 + omega * b.age * (1 + e);
    const nx = cx + Math.cos(th) * r;
    const ny = cy + Math.sin(th) * r;
    b.vx = (nx - b.x) / (dt * 60);
    b.vy = (ny - b.y) / (dt * 60);
  };
}

/**
 * 爆裂弹：atAge 秒时母弹消散，原地爆出 count 颗子弹（扇形总宽 spread、speed、color）
 * ——孢子囊/种子荚的"囊→飞→裂→孢"生命循环（S4 森林）。
 *  field 在生成时以闭包捕获（同 splitBullet/inkBullet）；
 *  age 窗口触发（prevAge < atAge <= age 那一帧），无 per-bullet 状态，池复用安全。
 */
export function burstBullet(field, atAge, { count = 3, spread = 0.9, speed = 1.2, r = 3.5, color = 'orb_green', full = false } = {}) {
  return (b, dt) => {
    if (b.age >= atAge && b.age - dt < atAge) {
      const base = Math.atan2(b.vy, b.vx);
      for (let i = 0; i < count; i++) {
        // full：全向环裂（开花弹——母弹到点向四周开成花，别家分裂的经典形态）
        const a = full ? base + (i / count) * TAU : base + (count === 1 ? 0 : (i / (count - 1) - 0.5) * spread);
        field.spawn({ x: b.x, y: b.y, speed, angle: a, r, color });
      }
      field.pool.release(b);
    }
  };
}

/**
 * 分裂弹：atAge 秒时原地分裂出 ±spread 两颗子弹，母弹继续飞行（S6）。
 * field 在生成时以闭包捕获（onUpdate 的 ctx 不含 field）。
 */
export function splitBullet(field, atAge = 1.0, spread = 0.5, color = 'star_pink') {
  return (b, dt) => {
    // age 窗口触发（prevAge < atAge <= age 那一帧裂一次）——无 per-bullet 状态，
    // 池复用安全（旧 _split 懒标记会让回收弹不再分裂：'每次都是只有一个子弹'）
    if (b.age >= atAge && b.age - dt < atAge) {
      const base = Math.atan2(b.vy, b.vx);
      for (const k of [-1, 1]) {
        field.spawn({ x: b.x, y: b.y, speed: 2.4, angle: base + k * spread, r: 3, color });
      }
    }
  };
}

/** 分裂母弹齐射：count 颗母弹以 aim 为中心 fan 散开，各在 atAge 裂成 ±spread 两子（母弹续飞）。
 *  count=1 正对 aim（原模板，配菜卡 2 颗、签名卡 3 颗——'分裂'要有 volley 的样子）。 */
export function splitMotherVolley(ctx, x, y, aim, { count = 1, fan = 0.35, speed = 2.2, r = 5, color = 'star_yellow', atAge = 1.0, spread = 0.5, childColor = 'star_pink', mode = 'split', burstCount = 8, burstSpeed = 1.6, chain = 0 } = {}) {
  for (let m = 0; m < count; m++) {
    const a = aim + (m - (count - 1) / 2) * fan;
    const at = atAge + m * chain; // chain>0：链式逐裂（引线感，"变换"主题）
    ctx.field.spawn({
      x, y, speed, angle: a, r, color,
      onUpdate: mode === 'burst'
        ? burstBullet(ctx.field, at, { count: burstCount, speed: burstSpeed, r: 3.5, color: childColor, full: true }) // 环裂开花
        : splitBullet(ctx.field, at, spread, childColor),
    });
  }
}

/** 定时链弹齐射（S3）：朝 aim 扇形 5 发，依次延迟突进。 */
export function chainVolley(field, x, y, aim, opts = {}) {
  const n = opts.n || 5;
  const arc = opts.arc !== undefined ? opts.arc : 0.8;
  const spd = opts.speed || 3.2;
  for (let i = 0; i < n; i++) {
    const ang = aim - arc / 2 + (n === 1 ? arc / 2 : (i / (n - 1)) * arc);
    field.spawn({
      x, y, speed: 1.2, angle: ang, r: opts.r || 4, color: opts.color || 'pill_yellow',
      onUpdate: chainBullet(0.5 + i * (opts.step || 0.18), ang, spd),
    });
  }
}

// ---------------------------------------------------------------- Boss 工厂

function bossDef(name, title, spriteKey, phases) {
  return { name, title, spriteKey, x: VIEW_W / 2, y: -48, phases };
}

// ============================== STAGE 1 云端·天空 ==============================
// 主题弹幕：直线狙弹 + 稀疏扩散（教学）。同屏弹量 ≤60。

/** 中 boss：风暴精灵（4 阶段） */
export function s1Mid() {
  return Object.assign(bossDef('风暴精灵', '云端的中枢', 's1_mid_air_elemental', [
    {
      name: '旋风', spellCard: false, hp: 600, timeLimit: 30,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 90, 70, 0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 阵风回廊：两侧交替吹来的横向弹流（留缺口，走位引导）
        if (fireTimer(boss, dt, 1.6)) {
          const fromLeft = Math.floor(boss.phaseTime / 1.6) % 2 === 0;
          const n = P.diffCount(8);
          // 行距随弹数自适应：固定 56px 在高密度下会超出屏底被回收，
          // 缺口可能落在不可见区（历史 bug：Hard/Lunatic 过半波次无缺口）
          const span = Math.min(56 * (n - 1), 480);
          const step = n > 1 ? span / (n - 1) : 0;
          const gap = 2 + (Math.floor(boss.phaseTime) % Math.max(1, n - 4));
          for (let i = 0; i < n; i++) {
            if (i === gap || i === gap + 1) continue;
            ctx.field.spawn({
              x: fromLeft ? -8 : VIEW_W + 8, y: 60 + i * step,
              speed: 2.2, angle: fromLeft ? 0 : Math.PI, r: 4, color: 'drop_cyan',
            });
          }
        }
        if (fireTimer(boss, dt, 2.8, '_ft2')) {
          P.aimed(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 3, speed: 2.4, color: 'orb_red' });
        }
      },
    },
    {
      name: '风符「旋涡气流」', spellCard: true,
      desc: "我绕着自己打转，风就跟着我打转——一会儿左旋，一会儿右旋。看傻了可不行，涡流会把你一起卷进去的哦。", hp: 900, timeLimit: 45,
      enter: enterAt(240, 150),
      update(boss, dt, ctx) {
        // 旋涡气流：boss 自身绕场心公转，双旋臂随之拖出涡流（与岚符的定点风暴区分）
        // 签名：旋向每 4s 反转 + 臂数 2/3 换挡（涡流聚散）
        const cx = 240 + Math.cos(boss.phaseTime * 0.9) * 100;
        const cy = 150 + Math.sin(boss.phaseTime * 0.9) * 30;
        bossMove(boss, dt, cx, cy, 130, 0, 0);
        const aim = aimOf(ctx, boss.x, boss.y);
        if (fireTimer(boss, dt, 0.16)) {
          P.spiral(ctx.field, boss.x, boss.y, boss.phaseTime, aim, {
            arms: seq(boss.phaseTime, 6, [2, 3]), spin: 2.5 * flip(boss.phaseTime, 4), speed: 1.8, color: 'drop_cyan',
          });
        }
      },
    },
    {
      name: '乱流', spellCard: false, hp: 550, timeLimit: 28,
      enter: enterAt(240, 130),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 100, 130, 0, 1.2);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 乱流：左右交替的湍流抛掷（曲线放缓 + 风摆抖动，像树叶翻滚，与旋风的直线稳风、旋涡气流的有序涡流区分）
        if (fireTimer(boss, dt, 1.2)) {
          const volley = P.diffCount(5);
          const dir = Math.floor(boss.phaseTime / 1.2) % 2 === 0 ? 1 : -1;
          for (let i = 0; i < volley; i++) {
            const a = aim + (i - (volley - 1) / 2) * 0.22;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 2.2, angle: a, r: 4, color: 'drop_cyan', onUpdate: composeBullets(curveBullet(dir * 0.5), driftBullet(0.4, 2.5)) });
          }
        }
        if (fireTimer(boss, dt, 2.0, '_ft2')) {
          P.aimed(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 3, speed: 2.6, color: 'orb_red' });
        }
      },
    },
    {
      name: '飓符「台风眼壁」', spellCard: true,
      desc: "台风里最安全的地方是风眼。我把风墙转着压过去，只给你留一个口——敢不敢钻进来，和我面对面？", hp: 900, timeLimit: 45,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 80, 90, 10);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 台风眼壁：朝自机留缺口的旋转风墙（钻眼走位，与旋涡气流的拖尾涡流区分）
        // 签名：发射间隔呼吸（1.3±0.45s）+ 风墙每 5s 换向
        if (fireTimer(boss, dt, 1.3 + Math.sin(boss.phaseTime) * 0.45)) {
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 16, speed: 1.5, offset: sweepAngle(boss.phaseTime, 0.6, 5), color: 'drop_cyan', gapAt: { width: 0.35 } });
        }
        if (fireTimer(boss, dt, 2.2, '_ft2')) {
          P.aimed(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 3, speed: 2.5, color: 'orb_red' });
        }
      },
    },
  ]), { mid: true });
}

/** 大 boss：积雨云之王（8 阶段） */
export function s1Big() {
  return bossDef('积雨云之王', '天空的支配者', 's1_boss_stormlord', [
    {
      name: '积雨压顶', spellCard: false, hp: 600, timeLimit: 30,
      enter: enterAt(240, 130),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 80, 120, 0);
        // 积雨压顶：云层在自机头顶崩落（宽带落弹，向自机位置收拢）
        // 签名：崩落间隔 1.0→1.6s 呼吸（云层聚散）
        if (fireTimer(boss, dt, osc(boss.phaseTime, 5, 1.0, 1.6))) {
          const n = P.diffCount(9);
          const cx = ctx.playerX !== undefined ? ctx.playerX : 240;
          for (let i = 0; i < n; i++) {
            const spread = (i / (n - 1) - 0.5) * 360;
            // x 钳到生成窗内：贴边位崩落弹在屏外生成会被当帧回收，密度凭空变稀（历史 bug）
            const sx = Math.max(16, Math.min(464, cx + spread * 0.55 + randRange(-12, 12)));
            ctx.field.spawn({
              x: sx, y: -8,
              speed: randRange(1.6, 2.4), angle: Math.PI / 2 + spread * 0.0006, r: 4, color: 'orb_blue',
            });
          }
        }
      },
    },
    {
      name: '云符「积雨乱打」', spellCard: true,
      desc: "云越积越厚，雨越下越急。我的积雨云从来不一次下完，它喜欢先试探，再倾盆。", hp: 900, timeLimit: 45,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 80, 100, 12);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 签名：环弹 8→14 爬坡（积雨渐强渐弱）+ 雨速呼吸；雨加风向（风雨交加，逐波换向）
        if (fireTimer(boss, dt, 1.0)) {
          const lean = 0.35 * flip(boss.phaseTime, 3);
          P.rain(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 7, speedMin: 1.5, speedMax: osc(boss.phaseTime, 6, 2.2, 3.0), lean, color: 'drop_cyan' });
        }
        if (fireTimer(boss, dt, 1.8, '_ft2')) {
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: tri(boss.phaseTime, 6, 8, 14), speed: 1.6, offset: boss.phaseTime, color: 'orb_blue' });
        }
      },
    },
    {
      name: '雷电走线', spellCard: false, hp: 550, timeLimit: 28,
      enter: enterAt(240, 120),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 110, 150, 0, 1.1);
        const aim = aimOf(ctx, boss.x, boss.y);
        if (fireTimer(boss, dt, 1.0)) {
          // 雷电走线：蛇形抖动的高速狙（闪电质感）
          const n = P.diffCount(5);
          for (let i = 0; i < n; i++) {
            const a = aim + (i - (n - 1) / 2) * 0.14;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 3.0, angle: a, r: 4, color: 'pill_yellow', onUpdate: zigzagBullet(1.6, 9) });
          }
        }
        if (fireTimer(boss, dt, 2.4, '_ft2')) {
          P.rain(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 6, speedMax: 2.8, color: 'drop_cyan' });
        }
      },
    },
    {
      name: '岚符「天罚风暴」', spellCard: true,
      desc: "旋臂、花瓣、暴雨一齐上场的时候，连我自己都要让三分。这是天空的全力，你姑且当作天罚来躲吧。", hp: 1000, timeLimit: 50,
      enter: enterAt(240, 150),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 80, 60, 10);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 签名：旋速 2.4→3.6 呼吸（风暴增强）+ 花瓣 4/5/6 轮换
        if (fireTimer(boss, dt, 0.3)) {
          P.spiral(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { arms: 3, spin: osc(boss.phaseTime, 10, 2.4, 3.6), speed: 2.0, color: 'orb_blue' });
        }
        if (fireTimer(boss, dt, 2.4, '_ft2')) {
          P.flower(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 10, petals: seq(boss.phaseTime, 8, [4, 5, 6]), speed: 1.8, amp: 0.6, color: 'star_purple' });
        }
        if (fireTimer(boss, dt, 2.8, '_ft3')) {
          P.rain(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 6, speedMax: 2.5, color: 'drop_cyan' });
        }
        // Hard+ 追加组件：自机狙连发层
        if (ctx.diffIdx >= 2 && fireTimer(boss, dt, 3.4, '_ft4')) {
          P.aimed(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 5, speed: 2.8, spread: 0.1, color: 'pill_yellow' });
        }
      },
    },
    {
      name: '风切', spellCard: false, hp: 600, timeLimit: 30,
      enter: enterAt(240, 130),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 110, 140, 0, 1.2);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 风切：交替内卷/外卷的曲线狙（与雷电走线的蛇形直狙区分）
        if (fireTimer(boss, dt, 1.1)) {
          const volley = P.diffCount(4);
          const dir = Math.floor(boss.phaseTime / 1.1) % 2 === 0 ? 1 : -1;
          for (let i = 0; i < volley; i++) {
            const a = aim + (i - (volley - 1) / 2) * 0.18;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 2.4, angle: a, r: 4, color: 'drop_cyan', onUpdate: curveBullet(dir * 1.0) });
          }
        }
        if (fireTimer(boss, dt, 2.0, '_ft2')) {
          P.spread(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 5, arc: 0.6, speed: 2.4, color: 'orb_blue' });
        }
      },
    },
    {
      name: '雷符「轰雷阵」', spellCard: true,
      desc: "雷不劈同一个地方？那是凡人的雷。我的落雷专挑你站的地方劈，半路还会拐弯。", hp: 1000, timeLimit: 50,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 90, 110, 0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 轰雷阵：预警落雷（先虚线预警轨迹，短延迟后实弹劈下）+ 蛇形闪电（与天罚风暴的旋臂花雨区分）
        // 签名：落雷点杀（预警 → 实弹）+ 蛇形幅度呼吸
        if (!boss._boltAt && fireTimer(boss, dt, 1.6)) {
          boss._boltAim = aim + osc(boss.phaseTime, 5, -0.4, 0.4);
          boss._boltAt = boss.phaseTime + 0.45;
          if (ctx.effects && ctx.effects.telegraphLine) ctx.effects.telegraphLine(boss.x, boss.y, boss._boltAim, '#ff4');
        }
        if (boss._boltAt && boss.phaseTime >= boss._boltAt) {
          boss._boltAt = 0;
          P.laserFan(ctx.field, boss.x, boss.y, boss.phaseTime, boss._boltAim, { n: 5, arc: 0.6, speed: 1.7, color: 'pill_yellow' });
        }
        if (fireTimer(boss, dt, 1.3, '_ft2')) {
          const n = P.diffCount(4);
          const amp = osc(boss.phaseTime, 6, 1.4, 2.2);
          for (let i = 0; i < n; i++) {
            const a = aim + (i - (n - 1) / 2) * 0.2;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 2.6, angle: a, r: 4, color: 'pill_yellow', onUpdate: zigzagBullet(amp, 7) });
          }
        }
        // 装饰：电荷火花慢雨（低威胁，青色电光点缀雷阵）
        if (fireTimer(boss, dt, 2.0, '_ft3')) {
          P.rain(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 5, speedMin: 0.8, speedMax: 1.3, drift: 0.3, r: 3, color: 'drop_cyan', ttl: 9 });
        }
      },
    },
    {
      name: '云海', spellCard: false, hp: 550, timeLimit: 28,
      enter: enterAt(240, 150),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 70, 120, 14);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 云海：慢速大云团环压场（与积雨压顶的头顶崩落区分）
        if (fireTimer(boss, dt, 1.5)) {
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 8, speed: 1.0, r: 7, offset: boss.phaseTime * 0.5, color: 'orb_cyan' });
        }
        if (fireTimer(boss, dt, 1.9, '_ft2')) {
          P.spread(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 5, arc: 0.7, speed: 2.2, color: 'drop_cyan' });
        }
      },
    },
    {
      name: '旋符「大旋风」', spellCard: true,
      desc: "暴风雨的终曲是一支圆舞。风往哪边转，雨就往哪边倒——跟上节拍，或者被我卷走。", hp: 1000, timeLimit: 50,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 80, 100, 10, 0.9);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 大旋风：风暴旋臂 + 倾盆暴雨（积雨云之王的暴风雨，与天罚风暴的花弹雨区分）
        // 签名：旋臂每 5s 换向 + 雨量 5→9 爬坡 + 雨被旋风卷着走（lean 随旋臂旋向）
        const spin = 2.4 * flip(boss.phaseTime, 5);
        if (fireTimer(boss, dt, 0.3)) {
          P.spiral(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { arms: 3, spin, speed: 1.8, color: 'drop_cyan' });
        }
        // 风眼双层：内层反向慢旋臂（深蓝，与主旋臂对绞）
        if (fireTimer(boss, dt, 0.45, '_ft0')) {
          P.spiral(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { arms: 2, spin: -spin * 0.7, speed: 1.3, color: 'drop_blue' });
        }
        // 无害风叶流：亮青碎叶顺旋向飞（harmless 装饰层，只供华丽）
        if (fireTimer(boss, dt, 0.2, '_ft3')) {
          P.spiral(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { arms: 3, spin: spin * 1.4, speed: 2.6, r: 2, color: 'drop_green', harmless: true });
        }
        if (fireTimer(boss, dt, 1.6, '_ft2')) {
          const lean = Math.sign(spin) * 0.3; // 雨斜向与旋臂旋向一致，像被旋风卷着走
          P.rain(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: tri(boss.phaseTime, 7, 6, 10), speedMin: 1.6, speedMax: 2.6, lean, color: 'orb_blue' });
        }
      },
    },
  ]);
}

// ============================== STAGE 2 深海·水压 ==============================
// 主题弹幕：慢速大弹（r6-9, 0.9-1.8）+ 涡旋（curveBullet）。

/** 中 boss：深海灯笼鱼（4 阶段） */
export function s2Mid() {
  return Object.assign(bossDef('深海灯笼鱼', '水压的引路者', 's2_mid_jellyfish', [
    {
      name: '诱光', spellCard: false, hp: 600, timeLimit: 30,
      enter: enterAt(240, 150),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 60, 80, 14);
        const aim = aimOf(ctx, boss.x, boss.y);
        if (fireTimer(boss, dt, 1.2)) {
          P.aimed(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 3, speed: 1.3, spread: 0.25, r: 7, color: 'orb_orange' });
        }
        if (fireTimer(boss, dt, 2.6, '_ft2')) {
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 8, speed: 1.1, r: 6, color: 'orb_cyan' });
        }
      },
    },
    {
      name: '灯符「深海诱光」', spellCard: true,
      desc: "深海里没有光，除了我这一盏。灯火明明灭灭地飘过来，跟着光走的孩子，最后都到我嘴里来了。", hp: 900, timeLimit: 45,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 60, 100, 0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 诱光：明灭隐身的追踪灯火（灯笼鱼主题，与克拉肯的涡符区分）
        // 签名：灯火 3.2s 统一明灭——亮时追踪、灭时（alpha 0.15 鬼火）断追踪直线漂
        if (fireTimer(boss, dt, 1.6)) {
          P.homing(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 2, speed: osc(boss.phaseTime, 6, 1.2, 1.8), homing: 0.8, r: 6, color: 'orb_yellow', onUpdate: composeBullets(guideBullet(6), flickerBullet(3.2, 0.15, false, () => boss.phaseTime)) });
        }
        // 闪光突袭：短促自机狙
        if (burstTimer(boss, dt, 0.45, 3, 1.6)) {
          P.aimed(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 3, speed: 2.4, r: 5, color: 'orb_orange' });
        }
        // 装饰：深海浮游光点（低威胁慢速青光，逐弹错相明灭，像零星浮游生物）
        if (fireTimer(boss, dt, 2.4, '_ft3')) {
          P.rain(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 5, speedMin: 0.5, speedMax: 0.9, drift: 0.3, r: 3, color: 'orb_cyan', ttl: 9, onUpdate: flickerBullet(2.6, 0.25, true) });
        }
      },
    },
    {
      name: '暗流', spellCard: false, hp: 600, timeLimit: 30,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 60, 100, 12);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 暗流：顶部慢速大弹落流 + 水流推挤的曲线狙（与诱光的定点灯环区分）
        if (fireTimer(boss, dt, 1.4)) {
          P.rain(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 6, speedMin: 1.0, speedMax: 1.8, drift: 0.25, r: 6, color: 'drop_blue' });
        }
        if (fireTimer(boss, dt, 1.8, '_ft2')) {
          const volley = P.diffCount(3);
          for (let i = 0; i < volley; i++) {
            const a = aim + (i - (volley - 1) / 2) * 0.3;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 1.8, angle: a, r: 6, color: 'orb_cyan', onUpdate: curveBullet(i % 2 === 0 ? 0.7 : -0.7) });
          }
        }
      },
    },
    {
      name: '渊符「深海沉光」', spellCard: true,
      desc: "沉下去的光是什么样子？大颗大颗，慢慢地开，像花在墨里绽。卷流会替我推你一把，别客气。", hp: 950, timeLimit: 48,
      enter: enterAt(240, 150),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 50, 80, 10, 0.8);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 沉光：慢速大弹花绽 + 涡流缓缓卷场（与诱光的追踪灯火区分）
        // 签名：卷曲率 0.4→1.0 呼吸 + 花弹大小交替（r6/r9）
        if (fireTimer(boss, dt, 0.5)) {
          const volley = P.diffCount(3);
          const rate = osc(boss.phaseTime, 7, 0.4, 1.0);
          for (let i = 0; i < volley; i++) {
            const a = boss.phaseTime * 0.9 + (i / volley) * TAU;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 1.2, angle: a, r: 7, color: 'orb_blue', onUpdate: curveBullet(rate) });
          }
        }
        if (fireTimer(boss, dt, 3.0, '_ft2')) {
          P.flower(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 10, petals: 5, speed: 1.4, amp: 0.7, r: seq(boss.phaseTime, 3, [6, 9]), color: 'orb_cyan' });
        }
        // 装饰：海底上升的气泡（低威胁，从底部缓缓上浮）
        if (fireTimer(boss, dt, 2.5, '_ft3')) {
          for (let i = 0; i < 4; i++) {
            ctx.field.spawn({ x: randRange(24, 456), y: 648, speed: randRange(0.7, 1.1), angle: -Math.PI / 2 + randRange(-0.15, 0.15), r: 3, color: 'drop_cyan' });
          }
        }
      },
    },
  ]), { mid: true });
}

/** 大 boss：深渊克拉肯（8 阶段） */
export function s2Big() {
  return bossDef('深渊克拉肯', '深海的主宰', 's2_boss_kraken', [
    {
      name: '触手横扫', spellCard: false, hp: 650, timeLimit: 32,
      enter: enterAt(240, 130),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 60, 110, 0);
        const aim = aimOf(ctx, boss.x, boss.y);
        if (fireTimer(boss, dt, 1.5)) {
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 10, speed: 1.4, r: 7, offset: boss.phaseTime, color: 'orb_blue' });
        }
        // 触手卷流：横扫甩出的交替曲线扇（与墨雨的落流、暗涌的追踪区分）
        if (fireTimer(boss, dt, 1.1, '_ft2')) {
          const volley = P.diffCount(3);
          const dir = Math.floor(boss.phaseTime / 1.1) % 2 === 0 ? 1 : -1;
          for (let i = 0; i < volley; i++) {
            const a = aim + (i - (volley - 1) / 2) * 0.35;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 1.6, angle: a, r: 7, color: 'orb_orange', onUpdate: curveBullet(dir * 0.8) });
          }
        }
      },
    },
    {
      name: '涡符「大漩涡」', spellCard: true,
      desc: "我的腕足搅一搅，海就转起来了。卷向说变就变——海流听我的，不听你的。", hp: 950, timeLimit: 48, dpsCap: 65, // 慢成型钳顶：3.2 涡可见
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 60, 70, 10);
        // 签名：排水漩涡——boss 下方海面 (240,300) 生成收缩环（200→30, 3.2s）。
        // 每波先出环形涟漪预告（虚点内吸 0.6s）再成弹——弹从画面中间出来的符必须给预警（防初见杀）。
        if (fireTimer(boss, dt, 1.3)) {
          if (ctx.effects && ctx.effects.telegraphRing) ctx.effects.telegraphRing(240, 300, 200, '#9df', 0.6);
          (boss._whirlQ = boss._whirlQ || []).push({ at: boss.phaseTime + 0.6, base: boss.phaseTime * 0.7 });
        }
        if (boss._whirlQ && boss._whirlQ.length && boss.phaseTime >= boss._whirlQ[0].at) {
          const q = boss._whirlQ.shift();
          const n = P.diffCount(9);
          const vx = 240, vy = 300;
          for (let i = 0; i < n; i++) {
            const th0 = q.base + (i / n) * TAU;
            ctx.field.spawn({
              x: vx + Math.cos(th0) * 200, y: vy + Math.sin(th0) * 200,
              speed: 0, angle: 0, r: 6, color: 'orb_blue',
              onUpdate: whirlpoolBullet(vx, vy, { r0: 200, r1: 30, dur: 3.2, omega: 2.4, theta0: th0 }),
            });
          }
        }
      },
    },
    {
      name: '墨云', spellCard: false, hp: 600, timeLimit: 30,
      enter: enterAt(240, 120),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 70, 130, 0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 签名：墨滴成大云——大紫滴（视觉1.8倍/判定r8）落下1s后滞涩成浑浊墨云，
        // 滞留2.8s再下沉淡散；云隙间紫狙钻出。（与孢子"悬停后散射让路"区分：这是"遮挡"。）
        if (fireTimer(boss, dt, 1.5)) {
          P.rain(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 6, speedMin: 1.0, speedMax: 1.6, r: 8, color: 'orb_purple', scale: 1.8, onUpdate: inkBullet(ctx.field) });
        }
        if (fireTimer(boss, dt, 1.4, '_ft2')) {
          P.aimed(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 3, speed: 1.8, r: 6, color: 'orb_purple' });
        }
      },
    },
    {
      name: '泡符「深渊呼吸」', spellCard: true,
      desc: "深渊也会呼吸。一呼一吸，花都跟着涨落。你屏住呼吸没用，它又不听你的。", hp: 1000, timeLimit: 50,
      enter: enterAt(240, 150),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 50, 90, 14);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 签名：花幅 0.8→1.5 呼吸（深渊呼吸）+ 慢环来回扫
        if (fireTimer(boss, dt, 1.1)) {
          P.flower(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 16, petals: 6, speed: 1.6, amp: osc(boss.phaseTime, 5, 0.8, 1.5), r: 6, color: 'orb_cyan' });
        }
        if (fireTimer(boss, dt, 2.5, '_ft2')) {
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 6, speed: 0.9, r: 9, offset: sweepAngle(boss.phaseTime, 0.7, 4), color: 'orb_blue', gapAt: { width: 0.35 } });
        }
      },
    },
    {
      name: '触手狂乱', spellCard: false, hp: 700, timeLimit: 35,
      enter: enterAt(240, 130),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 90, 140, 0, 1.3);
        // 触手狂乱：反向旋转的卷流环（与触手横扫的曲线扇区分）
        if (fireTimer(boss, dt, 0.35)) {
          const volley = P.diffCount(3);
          for (let i = 0; i < volley; i++) {
            const a = -boss.phaseTime * 2.0 + (i / volley) * TAU;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 1.7, angle: a, r: 6, color: 'orb_purple', onUpdate: curveBullet(-0.9) });
          }
        }
        const aim = aimOf(ctx, boss.x, boss.y);
        // Hard+ 追加组件：反向慢环
        if (ctx.diffIdx >= 2 && fireTimer(boss, dt, 3.0, '_ft3')) {
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 10, speed: 1.3, r: 6, offset: -boss.phaseTime, color: 'orb_cyan' });
        }
      },
    },
    {
      name: '潮符「怒涛压境」', spellCard: true,
      desc: "浪一排一排压过来，缺口换来换去。被追上的话，就当我给你一个拥抱吧——用八条腕足。", hp: 1000, timeLimit: 50,
      enter: enterAt(240, 130),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 60, 100, 0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 怒涛压境：浪潮般的弹幕墙一排排压下（与大漩涡的卷流区分）
        // 签名：墙缺口左-中-右轮换 + 墙间隔 1.8→2.4 潮起潮落
        if (fireTimer(boss, dt, tri(boss.phaseTime, 10, 1.8, 2.4))) {
          P.wall(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 22, speed: 1.6, gapIndex: seq(boss.phaseTime, 2.2, [0.15, 0.5, 0.8]), r: 6, color: 'orb_blue' });
        }
        if (fireTimer(boss, dt, 1.4, '_ft2')) {
          P.aimed(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 3, speed: 1.8, r: 7, color: 'orb_orange' });
        }
      },
    },
    {
      name: '暗涌', spellCard: false, hp: 650, timeLimit: 32,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 70, 110, 10, 1.0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 暗涌：水下潜行的追踪暗流（远处不显形、逼近 210px 才现形）+ 交错卷流（一明一暗）
        if (fireTimer(boss, dt, 1.8)) {
          P.homing(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 3, speed: 1.5, homing: 0.7, r: 6, color: 'orb_purple', onUpdate: composeBullets(guideBullet(6), shadowBullet(210)) });
        }
        if (fireTimer(boss, dt, 1.3, '_ft2')) {
          const volley = P.diffCount(4);
          for (let i = 0; i < volley; i++) {
            const a = aim + (i - (volley - 1) / 2) * 0.3;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 1.7, angle: a, r: 6, color: 'orb_cyan', onUpdate: curveBullet(i % 2 === 0 ? 0.8 : -0.8) });
          }
        }
      },
    },
    {
      name: '漩符「螺旋海流」', spellCard: true,
      desc: "见过大漩涡长出骨架的样子吗？旋臂是骨，卷流是肉。这张网撒出去，连我自己都收不回来。", hp: 1050, timeLimit: 52,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 60, 80, 12, 0.8);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 螺旋海流：慢速大弹旋臂 + 涡旋卷流（曲线卷流呼应大漩涡，旋臂主轴区分）
        // 签名：旋臂每 4s 换向 + 卷流率 -0.5→-1.1 呼吸
        if (fireTimer(boss, dt, 0.25)) {
          P.spiral(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { arms: 3, spin: 2.0 * flip(boss.phaseTime, 4), speed: 1.3, r: 7, color: 'orb_blue' });
        }
        // 巨型慢弹副臂：视觉 1.6 倍的巨泡（大小弹混编，主轴的"大"字招牌）
        if (fireTimer(boss, dt, 0.5, '_ft0')) {
          P.spiral(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { arms: 2, spin: 2.0 * flip(boss.phaseTime, 4), speed: 1.0, r: 7, scale: 1.6, color: 'orb_cyan' });
        }
        if (fireTimer(boss, dt, 2.0, '_ft2')) {
          const volley = P.diffCount(4);
          const rate = osc(boss.phaseTime, 6, -0.5, -1.1);
          for (let i = 0; i < volley; i++) {
            const a = -boss.phaseTime * 0.8 + (i / volley) * TAU;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 1.4, angle: a, r: 7, color: 'orb_cyan', onUpdate: curveBullet(rate) });
          }
        }
        // 无害水泡：底部上冒（错相明灭，深海氛围装饰层）
        if (fireTimer(boss, dt, 1.2, '_ft3')) {
          for (let i = 0; i < 3; i++) {
            ctx.field.spawn({ x: randRange(24, 456), y: 648, speed: randRange(0.6, 1.0), angle: -Math.PI / 2 + randRange(-0.12, 0.12), r: 3, color: 'drop_cyan', harmless: true, ttl: 8, onUpdate: flickerBullet(2.2, 0.35, true) });
          }
        }
      },
    },
  ]);
}

// ============================== STAGE 3 齿轮·机械 ==============================
// 主题弹幕：激光（laserFan）+ 定时链弹（chainVolley）+ 弹幕墙（wall）。

/** 中 boss：哨戒机（4 阶段） */
export function s3Mid() {
  return Object.assign(bossDef('哨戒机', '齿轮回廊的守卫', 's3_mid_sentinel', [
    {
      name: '警戒扫描', spellCard: false, hp: 600, timeLimit: 30,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 100, 120, 0, 1.0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 警戒扫描：激光扫场 + 定时链弹警戒（与弹幕墙的墙压区分）
        // 签名：激光束绕自机来回扫（±0.4rad）
        if (fireTimer(boss, dt, 1.6)) {
          P.laserFan(ctx.field, boss.x, boss.y, boss.phaseTime, aim + osc(boss.phaseTime, 4, -0.4, 0.4), { n: 4, arc: 0.5, speed: 1.5, color: 'orb_red' });
        }
        if (fireTimer(boss, dt, 2.8, '_ft2')) {
          chainVolley(ctx.field, boss.x, boss.y, aim, { n: 3, arc: 0.6, speed: 3.0, color: 'pill_blue' });
        }
      },
    },
    {
      name: '锁符「回旋封锁」', spellCard: true,
      desc: "警告：本机封锁光束每五秒换向一次。规避窗口已计算完毕——不会告诉你的，自己算。", hp: 900, timeLimit: 45,
      enter: enterAt(240, 130),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 100, 90, 0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 回旋封锁：同速反向的红蓝双环半齿错位啮合（齿轮封锁；激光只在 p0/p2 出现）
        // 签名：红环正转/蓝环反转+半齿错位 + 环速 1.2→1.7 咬合升温
        if (fireTimer(boss, dt, 1.8)) {
          const off = boss.phaseTime * 0.9;
          const sp = osc(boss.phaseTime, 8, 1.2, 1.7);
          // n 给原始基准值：ring 内部会做难度缩放（diffCount 只用于直连 field.spawn 的自写循环）
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 9, speed: sp, offset: off, color: 'orb_red' });
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 9, speed: sp, offset: -off + Math.PI / 9, color: 'pill_blue' });
        }
        if (fireTimer(boss, dt, 2.4, '_ft2')) {
          P.aimed(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 2, speed: 2.2, spread: 0.12, color: 'orb_red' });
        }
      },
    },
    {
      name: '链齿', spellCard: false, hp: 550, timeLimit: 28,
      enter: enterAt(240, 130),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 100, 110, 0, 1.1);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 链齿：定时链弹咬合齐射 + 激光点杀（与警戒扫描的链弹警戒区分）
        if (fireTimer(boss, dt, 1.8)) {
          chainVolley(ctx.field, boss.x, boss.y, aim, { n: 6, arc: 0.7, speed: 3.0 });
        }
        if (fireTimer(boss, dt, 2.4, '_ft2')) {
          P.laserFan(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 4, arc: 0.5, speed: 1.7, color: 'orb_red' });
        }
      },
    },
    {
      name: '锯符「回旋锯阵」', spellCard: true,
      desc: "锯片臂数：三、四、五。转速：呼吸变速。警告：本机不对被切割者的体验负责。", hp: 900, timeLimit: 45,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 90, 90, 0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 回旋锯阵：四臂圆锯旋转切割 + 激光点杀（与回旋封锁的激光束扫场区分）
        // 签名：臂数 3/4/5 换挡（锯阵变速）+ 转速呼吸
        if (fireTimer(boss, dt, 0.25)) {
          P.spiral(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { arms: seq(boss.phaseTime, 6, [3, 4, 5]), spin: osc(boss.phaseTime, 7, 2.4, 3.2), speed: 2.0, color: 'orb_orange' });
        }
        if (fireTimer(boss, dt, 2.0, '_ft2')) {
          P.laserFan(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 4, arc: 0.5, speed: 1.7, color: 'orb_red' });
        }
      },
    },
  ]), { mid: true });
}

/** 大 boss：核心熔炉（8 阶段） */
export function s3Big() {
  return bossDef('核心熔炉', '机械城的心脏', 's3_boss_furnace', [
    {
      name: '齿轮齐射', spellCard: false, hp: 600, timeLimit: 30,
      enter: enterAt(240, 130),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 80, 80, 0);
        const aim = aimOf(ctx, boss.x, boss.y);
        if (fireTimer(boss, dt, 1.2)) {
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 14, speed: 2.0, offset: boss.phaseTime * 0.5, color: 'orb_orange' });
        }
        if (fireTimer(boss, dt, 2.4, '_ft2')) {
          P.laserFan(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 3, arc: 0.4, speed: 1.6, color: 'orb_red' });
        }
      },
    },
    {
      name: '链符「定时连锁」', spellCard: true,
      desc: "听，咔、咔——停。齿轮咬两扣，歇一口气。这就是我心跳的声音，你跟得上吗？", hp: 950, timeLimit: 48,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 80, 110, 0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 签名：链弹 2 连发（0.7s）后歇 1.8s（连锁咬合）+ 环弹来回扫
        if (burstTimer(boss, dt, 0.7, 2, 1.8)) {
          chainVolley(ctx.field, boss.x, boss.y, aim, { n: 6, arc: 0.8, speed: 3.2 });
        }
        if (fireTimer(boss, dt, 2.8, '_ft2')) {
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 14, speed: 1.7, offset: sweepAngle(boss.phaseTime, 1.0, 4), color: 'pill_yellow' });
        }
        // 装饰：时钟刻度的逆向慢环（低威胁，橙色系咬合感）
        if (fireTimer(boss, dt, 3.5, '_ft3')) {
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 8, speed: 1.1, r: 3, offset: -boss.phaseTime * 0.6, color: 'orb_orange' });
        }
      },
    },
    {
      name: '冲压墙', spellCard: false, hp: 550, timeLimit: 28,
      enter: enterAt(240, 120),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 90, 100, 0, 1.2);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 签名：整墙悬停 0.7s（悬停即预警）后猛压下来（chainBullet 停→压）+ 缺口左-中-右轮换
        if (fireTimer(boss, dt, 1.8)) {
          P.wall(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 24, speed: 1.9, gapIndex: (boss.phaseTime * 0.12) % 1, color: 'pill_blue', onUpdate: chainBullet(0.7, Math.PI / 2, 3.4) }); // 缺口连续平移（东方墙类精髓：玩家跟缺口走）
        }
        if (fireTimer(boss, dt, 2.2, '_ft2')) {
          P.laserFan(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 5, arc: 0.7, speed: 1.5, color: 'orb_red' });
        }
      },
    },
    {
      name: '炉符「超临界炉心」', spellCard: true,
      desc: "温度上来了！旋臂越转越快，脉冲一圈比一圈密。炉心过载之前，要么你先退，要么我炸给你看。", hp: 1050, timeLimit: 52,
      enter: enterAt(240, 150),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 70, 60, 8);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 超临界炉心：炉心旋臂 + 脉冲环 + 弹幕墙（与齿轮齐射的环弹区分：旋臂主轴）
        // 签名：旋速 1.8→2.8 升温 + 脉冲环 10→18 爬坡
        if (fireTimer(boss, dt, 0.15)) {
          P.spiral(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { arms: 4, spin: osc(boss.phaseTime, 10, 1.8, 2.8), speed: 2.2, color: 'orb_red' });
        }
        if (fireTimer(boss, dt, 2.0, '_ft2')) {
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: tri(boss.phaseTime, 8, 10, 18), speed: 1.8, offset: boss.phaseTime * 0.7, color: 'orb_orange', gapAt: { width: 0.35 } });
        }
        if (fireTimer(boss, dt, 3.0, '_ft3')) {
          P.wall(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 22, speed: 1.8, color: 'pill_blue' });
        }
      },
    },
    {
      name: '蒸汽喷发', spellCard: false, hp: 800, timeLimit: 35, dpsCap: 55, // 慢成型钳顶：3.4 喷可见
      enter: enterAt(240, 130),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 100, 130, 0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 蒸汽喷发：4.2s 压力循环——蓄压（嘶嘶汽口，末尾变密预警）→ 全向双环齐爆 → 余压细射流。
        // 签名：压力-释放呼吸节奏（与超临界炉心的持续升温、飞散齿轮的离心弯环区分）
        const cyc = (boss.phaseTime + 3.0) % 4.2; // 相位前移：首喷 1.2s（蓄压在入场无敌帧完成）
        if (boss._pc !== undefined && cyc < boss._pc) {
          // 喷发：双速环齐爆，速度差分离出压力波前/后缘
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 16, speed: 2.8, color: 'orb_orange' });
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 16, speed: 2.1, offset: Math.PI / 16, color: 'pill_yellow' });
        }
        boss._pc = cyc;
        // 余压射流：喷发后 0.9s 窗口，细射流直取（直连 spawn 保 n:1）
        if (cyc < 0.9 && fireTimer(boss, dt, 0.18, '_ftJet')) {
          ctx.field.spawn({ x: boss.x, y: boss.y, speed: 3.8, angle: aim, r: 4, color: 'orb_yellow' });
        }
        // 蓄压汽口：嘶嘶水汽（低威胁小弹侧上飘、闪烁），最后 0.8s 变密预警
        if (cyc > 1.2 && fireTimer(boss, dt, cyc > 3.4 ? 0.22 : 0.55, '_ftHiss')) {
          for (let i = 0; i < 2; i++) {
            const a = -Math.PI / 2 + (i === 0 ? -1 : 1) * randRange(0.3, 0.9);
            ctx.field.spawn({ x: boss.x + randRange(-30, 30), y: boss.y, speed: randRange(0.5, 0.9), angle: a, r: 3, color: 'orb_cyan', onUpdate: flickerBullet(1.5, 0.3, true) });
          }
        }
      },
    },
    {
      name: '导符「制导导弹」', spellCard: true,
      desc: "导弹一旦锁定，只会越咬越死，甩是甩不掉的。唯一的办法是别站着不动——虽然是废话。", hp: 1000, timeLimit: 50,
      enter: enterAt(240, 130),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 90, 100, 0, 1.0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 制导导弹：追踪弹齐射 + 激光封位（与定时连锁的延迟突进区分）
        // 签名：追踪强度 0.5→0.95 锁定加深 + 激光来回扫
        if (fireTimer(boss, dt, 1.5)) {
          P.homing(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 3, speed: 1.8, homing: osc(boss.phaseTime, 6, 0.5, 0.95), color: 'pill_yellow', onUpdate: guideBullet(6) });
        }
        if (fireTimer(boss, dt, 2.6, '_ft2')) {
          P.laserFan(ctx.field, boss.x, boss.y, boss.phaseTime, aim + osc(boss.phaseTime, 5, -0.4, 0.4), { n: 5, arc: 0.6, speed: 1.6, color: 'orb_red' });
        }
        // 装饰：发射警戒灯慢环（低威胁，橙色警示灯呼应导弹主题）
        if (fireTimer(boss, dt, 3.0, '_ft3')) {
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 8, speed: 1.1, r: 3, offset: boss.phaseTime * 0.5, color: 'orb_orange' });
        }
      },
    },
    {
      name: '铆钉连射', spellCard: false, hp: 600, timeLimit: 30,
      enter: enterAt(240, 120),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 110, 120, 0, 1.3);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 铆钉连射：扇形铆钉泼洒 + 短延迟链弹（与齿轮齐射的环弹区分）
        if (fireTimer(boss, dt, 1.2)) {
          P.spread(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 9, arc: 0.8, speed: 2.6, color: 'pill_blue' });
        }
        if (fireTimer(boss, dt, 2.4, '_ft2')) {
          chainVolley(ctx.field, boss.x, boss.y, aim, { n: 3, arc: 0.5, speed: 3.0 });
        }
      },
    },
    {
      name: '齿符「飞散齿轮」', spellCard: true,
      desc: "大齿轮小齿轮一起甩出去，离心力把它们掰成弯的。流水线嘛，偶尔也出点飞件。", hp: 1050, timeLimit: 52,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 80, 90, 10, 0.9);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 飞散齿轮：环状齿轮被离心力甩弯（曲线环，与超临界炉心的直线旋臂区分）
        // 签名：离心率 -0.5→-1.1 呼吸 + 大小齿轮混编（r4/r6）
        if (fireTimer(boss, dt, 0.4)) {
          const volley = P.diffCount(6);
          const rate = osc(boss.phaseTime, 6, -0.5, -1.1);
          const rr = seq(boss.phaseTime, 2, [4, 6]);
          const col = seq(boss.phaseTime, 3.6, ['orb_orange', 'pill_yellow']); // 双色齿轮交替
          for (let i = 0; i < volley; i++) {
            const a = -boss.phaseTime * 1.5 + (i / volley) * TAU;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 1.8, angle: a, r: rr, color: col, onUpdate: curveBullet(rate) });
          }
        }
        if (fireTimer(boss, dt, 2.2, '_ft2')) {
          P.laserFan(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 4, arc: 0.5, speed: 1.7, color: 'orb_red' });
        }
        // 无害咬合装饰环：红蓝对转齿轮（本关签名纹样，装饰层）
        if (fireTimer(boss, dt, 3.4, '_ft3')) {
          const n = 8;
          const off = boss.phaseTime * 0.9;
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n, speed: 1.2, offset: off, color: 'orb_red', harmless: true });
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n, speed: 1.2, offset: -off + Math.PI / n, color: 'pill_blue', harmless: true });
        }
        // 无害火花雨：炉心溅出的金红火星（快落小点，装饰层）
        if (fireTimer(boss, dt, 0.8, '_ft4')) {
          P.rain(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 4, speedMin: 2.4, speedMax: 3.6, drift: 0.2, r: 2, color: 'orb_orange', harmless: true, ttl: 4 });
        }
      },
    },
  ]);
}

// ============================== STAGE 4 腐叶·森林 ==============================
// 主题弹幕：曲线弹（curveBullet）+ 滞留孢子弹（sporeBullet）+ 藤蔓封位（wall）。

/** 中 boss：孢子菇后（4 阶段） */
export function s4Mid() {
  return Object.assign(bossDef('孢子菇后', '腐叶林的女王', 's4_mid_mushroom', [
    {
      name: '孢子散播', spellCard: false, hp: 600, timeLimit: 30,
      enter: enterAt(240, 150),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 60, 90, 10);
        const aim = aimOf(ctx, boss.x, boss.y);
        if (fireTimer(boss, dt, 1.6)) {
          const volley = P.holdRingCount(8, 5);
          for (let i = 0; i < volley; i++) {
            const a = (i / volley) * TAU + boss.phaseTime * 0.3;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 1.6, angle: a, r: 5, color: 'orb_green', onUpdate: sporeBullet(1.2) });
          }
        }
        if (fireTimer(boss, dt, 1.5, '_ft2')) {
          P.aimed(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 4, speed: 2.2, color: 'drop_pink' });
        }
      },
    },
    {
      name: '孢符「风媒飞孢」', spellCard: true,
      desc: "我的孩子们乘风去旅行，落在哪儿，就在哪儿住下。你也当心，别让它们落在你身上。", hp: 900, timeLimit: 45,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 60, 70, 0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 风媒飞孢：朝自机播撒的扇形孢子弹（滞留封位，与大 boss 的环状孢子区分）
        // 签名：滞留时长 1.0→1.8 呼吸 + 扇形张角 0.2→0.5 开合
        if (fireTimer(boss, dt, 1.4)) {
          const n = P.diffCount(3);
          const arc = osc(boss.phaseTime, 5, 0.2, 0.5);
          const hold = osc(boss.phaseTime, 6, 1.0, 1.8);
          for (let i = 0; i < n; i++) {
            const a = aim + (i - (n - 1) / 2) * arc;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 2.0, angle: a, r: 5, color: 'orb_green', onUpdate: sporeBullet(hold) });
          }
        }
        if (fireTimer(boss, dt, 2.4, '_ft2')) {
          P.aimed(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 3, speed: 2.5, color: 'orb_yellow' });
        }
        // 装饰：风媒花粉飘浮（低威胁慢速飘雨，呼应播撒主题）
        if (fireTimer(boss, dt, 2.0, '_ft3')) {
          P.rain(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 6, speedMin: 0.6, speedMax: 1.0, drift: 0.5, r: 3, color: 'drop_green', ttl: 9 });
        }
      },
    },
    {
      name: '菌丝缠绕', spellCard: false, hp: 650, timeLimit: 32,
      enter: enterAt(240, 130),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 80, 120, 0, 1.1);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 菌丝缠绕：扇形菌丝泼洒 + 滞留孢子缠绕封位（与孢子散播的环状孢子区分）
        if (fireTimer(boss, dt, 1.2)) {
          P.spread(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 5, arc: 0.7, speed: 2.2, r: 4, color: 'drop_green' });
        }
        if (fireTimer(boss, dt, 1.6, '_ft2')) {
          const volley = P.holdRingCount(6, 5);
          for (let i = 0; i < volley; i++) {
            const a = (i / volley) * TAU + boss.phaseTime * 0.4;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 1.6, angle: a, r: 5, color: 'orb_green', onUpdate: sporeBullet(1.5) });
          }
        }
      },
    },
    {
      name: '茸符「菌毯爆发」', spellCard: true,
      desc: "菌毯醒来的时候，花一丛一丛地开，孢子像雨一样落。踩过我的地毯，总要付出点代价。", hp: 900, timeLimit: 45, dpsCap: 60, dpsCapFirst: [6, 30], // 慢成型钳顶 + 开场 6s 减伤（菌毯展开慢）
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 70, 100, 12);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 菌毯爆发：版中菌环（fairy ring——菌毯长在 (240,320) 的地上，不是 boss 身上）。
        // 涟漪预告 0.5s → 三环生成在 r60/120/180 圆周上滞成毯，滞留后径向散射+下沉
        //（旧版悬停半径 27/49/70px 全在 boss 身边=无效威胁，玩家实测简单）
        if (fireTimer(boss, dt, 2.0)) {
          if (ctx.effects && ctx.effects.telegraphRing) ctx.effects.telegraphRing(240, 320, 180, '#8f8', 0.5);
          (boss._matQ = boss._matQ || []).push({ at: boss.phaseTime + 0.5, base: boss.phaseTime * 0.4 });
        }
        if (boss._matQ && boss._matQ.length && boss.phaseTime >= boss._matQ[0].at) {
          const q = boss._matQ.shift();
          // 慢弹密度原则：滞留环按实际半径给满可穿越上限（23/47/70），逐环错相防免费通道
          for (const [r0, n] of [[60, P.holdRingCount(12, 5, 60)], [120, P.holdRingCount(24, 5, 120)], [180, P.holdRingCount(36, 5, 180)]]) {
            for (let i = 0; i < n; i++) {
              const a = q.base + (i / n) * TAU + (r0 % 180 ? TAU / (2 * n) : 0);
              ctx.field.spawn({
                x: 240 + Math.cos(a) * r0, y: 320 + Math.sin(a) * r0,
                speed: 0.5, angle: a, r: 5, color: 'orb_green', onUpdate: sporeBullet(1.6),
              });
            }
          }
        }
        if (fireTimer(boss, dt, 2.8, '_ft2')) {
          P.aimed(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 2, speed: 2.3, color: 'drop_pink' });
        }
      },
    },
  ]), { mid: true });
}

/** 大 boss：千年树人（8 阶段） */
export function s4Big() {
  return bossDef('千年树人', '森林的古老意志', 's4_boss_treant', [
    {
      name: '落叶', spellCard: false, hp: 650, timeLimit: 32,
      enter: enterAt(240, 130),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 60, 110, 0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 落叶：腐叶翻飞——慢落 + driftBullet 大幅连贯横摆（rain 的 drift 只是出射角抖动，
        // 不是摆动；各归其位）+ 落叶携孢子滞留（与花粉风暴的花弹孢子区分）
        if (fireTimer(boss, dt, 0.9)) {
          P.rain(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 9, speedMin: 0.9, speedMax: 1.6, drift: 0.15, color: 'drop_orange', onUpdate: driftBullet(0.9, 1.6) });
        }
        if (fireTimer(boss, dt, 1.7, '_ft2')) {
          const volley = P.holdRingCount(8, 5);
          for (let i = 0; i < volley; i++) {
            const a = (i / volley) * TAU + boss.phaseTime * 0.4;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 1.5, angle: a, r: 5, color: 'orb_yellow', onUpdate: sporeBullet(1.6) });
          }
        }
      },
    },
    {
      name: '藤符「封位蔓牢」', spellCard: true,
      desc: "藤蔓不急。它先围起来，再把缺口一个个挪开。一千年了，没有谁跑得过耐心。", hp: 950, timeLimit: 48,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 60, 80, 0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 签名：墙缺口左-中-右轮换 + 孢子环转速呼吸
        if (fireTimer(boss, dt, 2.2)) {
          P.wall(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 24, speed: 1.6, gapIndex: (Math.sin(boss.phaseTime * 0.5) + 1) / 2, color: 'pill_green' }); // 缺口连续摆动（藤蔓摇曳）
        }
        if (fireTimer(boss, dt, 1.8, '_ft2')) {
          const volley = P.holdRingCount(8, 5);
          const w = osc(boss.phaseTime, 6, 0.3, 0.9);
          for (let i = 0; i < volley; i++) {
            const a = (i / volley) * TAU + boss.phaseTime * w;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 1.7, angle: a, r: 5, color: 'orb_green', onUpdate: sporeBullet(2.0) });
          }
        }
      },
    },
    {
      name: '花粉风暴', spellCard: false, hp: 600, timeLimit: 30,
      enter: enterAt(240, 150),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 70, 130, 10);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 花粉风暴：花瓣风暴 + 朝自机播撒的花粉孢子（与菌毯爆发的孢雨区分）
        if (fireTimer(boss, dt, 1.4)) {
          P.flower(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 14, petals: 5, speed: 1.9, amp: 0.9, color: 'orb_yellow' });
        }
        if (fireTimer(boss, dt, 1.2, '_ft2')) {
          const n = P.diffCount(3);
          for (let i = 0; i < n; i++) {
            const a = aim + (i - (n - 1) / 2) * 0.3;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 2.0, angle: a, r: 5, color: 'orb_yellow', onUpdate: sporeBullet(1.3) });
          }
        }
      },
    },
    {
      name: '根符「大地脉动」', spellCard: true,
      desc: "把手放在地上，你摸到的就是它的脉。一胀，一缩。我的根，比你想象的走得远。", hp: 1050, timeLimit: 52,
      enter: enterAt(240, 130),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 60, 90, 0, 0.9);
        // 大地脉动：根系曲线卷流 + 孢子滞留脉冲（与千年之怒的花弹区分）
        // 签名：发射间隔 0.16→0.28 脉动 + 孢子环 8→12 爬坡
        if (fireTimer(boss, dt, osc(boss.phaseTime, 4, 0.16, 0.28))) {
          const volley = P.diffCount(3);
          for (let i = 0; i < volley; i++) {
            const a = boss.phaseTime * 1.6 + (i / volley) * TAU;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 1.9, angle: a, r: 5, color: 'orb_green', onUpdate: curveBullet(-0.7) });
          }
        }
        if (fireTimer(boss, dt, 2.0, '_ft2')) {
          // 滞留孢子环：弹数按悬停半径可穿越封顶（holdRingCount），滞留结束后散射
          const volley = P.holdRingCount(tri(boss.phaseTime, 8, 8, 12), 5);
          for (let i = 0; i < volley; i++) {
            const a = (i / volley) * TAU + boss.phaseTime;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 1.6, angle: a, r: 5, color: 'orb_yellow', onUpdate: sporeBullet(1.8) });
          }
        }
      },
    },
    {
      name: '千年之怒', spellCard: false, hp: 700, timeLimit: 35,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 90, 140, 0, 1.4);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 千年之怒：跺怒节拍——每 2.5s 一跺快速震荡环（节拍即预警）+ 跺间余震落叶
        //（drift 高频 3Hz"被震得发抖"，与 p0 翻飞落叶的 1.6Hz 大摆区分）+ 怒目轻狙
        if (fireTimer(boss, dt, 2.5)) {
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 14, speed: 3.0, color: 'orb_yellow' });
        }
        if (fireTimer(boss, dt, 1.1, '_ft2')) {
          P.rain(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 4, speedMin: 1.0, speedMax: 1.8, drift: 0.2, r: 4, color: 'drop_orange', onUpdate: driftBullet(0.6, 3) });
        }
        if (fireTimer(boss, dt, 2.0, '_ft3')) {
          P.aimed(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 2, speed: 2.6, color: 'drop_pink' });
        }
      },
    },
    {
      name: '叶符「翠叶旋舞」', spellCard: true,
      desc: "落叶也会跳舞，转够了就换个方向。年轻人，别光看叶子好看——舞池里是不能停脚的。", hp: 1000, timeLimit: 50,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 70, 100, 12, 0.9);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 翠叶旋舞：绿叶旋臂层层展开（与大地脉动的曲线卷流区分）
        // 签名：旋臂每 4s 换向（旋舞）+ 狙弹张角呼吸
        if (fireTimer(boss, dt, 0.25)) {
          P.spiral(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { arms: 3, spin: 2.6 * flip(boss.phaseTime, 4), speed: 1.8, r: 5, color: 'orb_green' });
        }
        if (fireTimer(boss, dt, 1.5, '_ft2')) {
          P.aimed(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 3, speed: 2.5, spread: osc(boss.phaseTime, 5, 0.08, 0.2), color: 'drop_pink' });
        }
      },
    },
    {
      name: '荆棘', spellCard: false, hp: 750, timeLimit: 30, dpsCap: 55, // 慢成型钳顶：3.8 丛可见
      enter: enterAt(240, 130),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 90, 120, 0, 1.2);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 荆棘：扇形尖刺 + 生长荆棘丛——12 颗刺弹逐颗"长"出成排（左→右看得见的生长），
        // 滞留 2s 封路后散射让路；轮换缺口 + 两端边距可穿（与 S3 冲压墙的机械物理学区分）
        if (fireTimer(boss, dt, 1.3)) {
          P.spread(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 9, arc: 0.7, speed: 2.4, r: 4, color: 'drop_green' });
        }
        if (burstTimer(boss, dt, 0.08, 14, 2.6)) {
          const i = boss._thrn || 0;
          boss._thrn = i + 1;
          const gap = [2, 6, 10][Math.floor(i / 14) % 3];
          if (i % 14 !== gap && i % 14 !== gap + 1) {
            ctx.field.spawn({
              x: 60 + (i % 14) * 28, y: boss.y + 50, speed: 0.9, angle: Math.PI / 2, r: 5,
              color: 'drop_green', onUpdate: sporeBullet(2.0),
            });
          }
        }
      },
    },
    {
      name: '精符「森之精魄」', spellCard: true,
      desc: "林子里的小东西喜欢你，会一直跟着你。花开了又谢，谢了又开——它们可不懂什么叫客气。", hp: 1050, timeLimit: 52,
      enter: enterAt(240, 150),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 60, 90, 10, 0.8);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 森之精魄：怕生精魄——homing 正弦变号（追→退→追的开瓶钻轨迹，与诱灯明灭、
        // 导弹锁定加深区分）+ 花粉脉冲（与封位蔓牢的墙/滞留区分）
        // 签名：精魄弹速 1.3→1.9 游荡 + 花弹绿/黄交替（精魄变色）
        if (fireTimer(boss, dt, 1.3)) {
          P.homing(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 5, speed: osc(boss.phaseTime, 6, 1.3, 1.9), homing: 0.7, r: 5, color: 'orb_yellow', onUpdate: composeBullets(guideBullet(7), timidBullet(1.8)) });
        }
        if (fireTimer(boss, dt, 2.0, '_ft2')) {
          P.flower(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 18, petals: 5, speed: 1.7, amp: 0.8, color: seq(boss.phaseTime, 4, ['orb_green', 'orb_yellow']) });
        }
        // 无害荧光孢子：林间生物荧光（慢漂 + 错相明灭，装饰层）
        if (fireTimer(boss, dt, 1.6, '_ft4')) {
          for (let i = 0; i < 3; i++) {
            const a = randRange(0, TAU);
            ctx.field.spawn({ x: boss.x + Math.cos(a) * 40, y: boss.y + Math.sin(a) * 30, speed: randRange(0.3, 0.6), angle: randRange(0, TAU), r: 3, color: 'orb_green', harmless: true, ttl: 8, onUpdate: composeBullets(driftBullet(0.4, 1.2), flickerBullet(2.4, 0.25, true)) });
          }
        }
        // 装饰：落叶飘舞（低威胁慢速橙叶，精魄游荡的森林氛围）
        if (fireTimer(boss, dt, 2.4, '_ft3')) {
          P.rain(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 5, speedMin: 0.7, speedMax: 1.2, drift: 0.5, r: 4, color: 'orb_orange', ttl: 9 });
        }
      },
    },
  ]);
}

// ============================== STAGE 5 幽影·暗界 ==============================
// 主题弹幕：自机狙奇袭 + 暗色低可见弹（purple/blue 小弹）+ 加速弹（accelBullet）。

/** 中 boss：提灯鬼（4 阶段） */
export function s5Mid() {
  return Object.assign(bossDef('提灯鬼', '暗界的引魂人', 's5_mid_lanterneye', [
    {
      name: '鬼火引诱', spellCard: false, hp: 600, timeLimit: 30,
      enter: enterAt(240, 150),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 110, 130, 0, 1.2);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 鬼火引诱：飘忽蛇形弹 + 摇曳追踪鬼火（drift 小摆，飘忽地追；与奇袭的边缘伏击区分）
        if (fireTimer(boss, dt, 0.9)) {
          const n = P.diffCount(3);
          for (let i = 0; i < n; i++) {
            const a = aim + (i - (n - 1) / 2) * 0.18;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 2.4, angle: a, r: 3, color: 'orb_purple', onUpdate: zigzagBullet(2.0, 5) });
          }
        }
        if (fireTimer(boss, dt, 2.4, '_ft2')) {
          P.homing(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 3, speed: 1.6, homing: 0.5, r: 4, color: 'drop_pink', onUpdate: composeBullets(guideBullet(6), driftBullet(0.35, 2.2)) });
        }
      },
    },
    {
      name: '灯符「鬼火提灯」', spellCard: true,
      desc: "提灯一晃，鬼火就飘过去了。忽明忽暗，才是引路的样子嘛。跟紧哦——跟丢了可不怪我。", hp: 900, timeLimit: 45,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 90, 100, 0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 鬼火：成对摇曳追踪灯火（drift 最招摇，鬼火飘摆诱敌；与虚无之眼的环弹海区分）
        // 签名：追踪强度 0.5→0.9 忽明忽暗 + 加速鬼火 2 连发（0.9s）后歇 1.8s
        if (fireTimer(boss, dt, 1.2)) {
          P.homing(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 3, speed: 1.7, homing: osc(boss.phaseTime, 5, 0.5, 0.9), r: 4, color: 'drop_pink', onUpdate: composeBullets(guideBullet(6), driftBullet(0.5, 2.8)) });
        }
        // 提灯脉冲：加速鬼火三连（与百鬼夜行的旋臂队列区分）
        if (burstTimer(boss, dt, 0.9, 2, 1.8)) {
          for (const k of [-1, 0, 1]) {
            const a = aim + k * 0.15;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 1.6, angle: a, r: 4, color: 'orb_purple', onUpdate: accelBullet(1.0) });
          }
        }
        // 装饰：磷火飘浮（低威胁慢速蓝火，衬托鬼火主题）
        if (fireTimer(boss, dt, 2.2, '_ft3')) {
          P.rain(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 4, speedMin: 0.7, speedMax: 1.1, drift: 0.35, r: 3, color: 'orb_blue', ttl: 9 });
        }
      },
    },
    {
      name: '奇袭', spellCard: false, hp: 650, timeLimit: 32,
      enter: enterAt(240, 120),
      update(boss, dt, ctx) {
        // 平缓左右扫掠，走位引导自机跟枪（低速可读，拖影由 Boss 类提供）
        const tx = 240 + Math.sin(boss.phaseTime * 0.9) * 110;
        bossMove(boss, dt, tx, boss._ty, 110, 0, 0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 奇袭：边缘伏击——两侧屏边 telegraph 虚线预警（0.5s），暗刃沿线突进直取预警时
        // 自机位置（弹不从 boss 出，从黑暗里杀出来；与鬼火引诱的追踪、百鬼夜行的旋臂区分）
        if (fireTimer(boss, dt, 2.2)) {
          const n = P.diffCount(3);
          boss._ambush = boss._ambush || [];
          for (let i = 0; i < n * 2; i++) {
            const x = i % 2 === 0 ? -8 : VIEW_W + 8;
            const y = randRange(150, 450);
            const a = aimOf(ctx, x, y);
            boss._ambush.push({ x, y, a, at: boss.phaseTime + 0.5 });
            if (ctx.effects && ctx.effects.telegraphLine) ctx.effects.telegraphLine(x, y, a, '#c6f');
          }
        }
        if (boss._ambush && boss._ambush.length) {
          const due = boss._ambush.filter((p) => boss.phaseTime >= p.at);
          boss._ambush = boss._ambush.filter((p) => boss.phaseTime < p.at);
          for (const p of due) {
            ctx.field.spawn({ x: p.x, y: p.y, speed: 3.6, angle: p.a, r: 4, color: 'orb_purple' });
          }
        }
        // 轻狙：boss 本体不作壁上观（低速可读）
        if (fireTimer(boss, dt, 2.8, '_ft2')) {
          P.aimed(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 1, speed: 2.4, r: 3, color: 'orb_purple' });
        }
      },
    },
    {
      name: '影符「百鬼夜行」', spellCard: true,
      desc: "夜行的队伍出发咯。小鬼火三三两两地走，走得慢的可别掉队。哎呀，你也在队伍里吗？", hp: 950, timeLimit: 48,
      enter: enterAt(240, 130),
      update(boss, dt, ctx) {
        // 百鬼夜行：boss 缓慢横移，暗色小弹旋臂如鬼火队列 + 追踪鬼火（与鬼火提灯的加速鬼火区分）
        const tx = 240 + Math.sin(boss.phaseTime * 0.7) * 100;
        bossMove(boss, dt, tx, boss._ty, 100, 0, 0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 签名：旋速 -1.8→-3.0 起伏（夜行速度）+ 摇曳追踪鬼火 2/3 交替（drift 长摆夜行）
        if (fireTimer(boss, dt, 0.2)) {
          P.spiral(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { arms: 3, spin: osc(boss.phaseTime, 8, -1.8, -3.0), speed: 1.9, r: 3, color: 'orb_purple' });
        }
        if (fireTimer(boss, dt, 1.6, '_ft2')) {
          P.homing(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: seq(boss.phaseTime, 6, [2, 3]), speed: 1.7, homing: 0.6, r: 4, color: 'drop_pink', onUpdate: composeBullets(guideBullet(6), driftBullet(0.4, 1.8)) });
        }
      },
    },
  ]), { mid: true });
}

/** 大 boss：虚无之眼（8 阶段） */
export function s5Big() {
  return bossDef('虚无之眼', '暗界尽头的凝视', 's5_boss_voideye', [
    {
      name: '凝视', spellCard: false, hp: 600, timeLimit: 30,
      enter: enterAt(240, 130),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 80, 90, 0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 签名：狙弹 3→5 渐强（凝视压迫）
        if (fireTimer(boss, dt, 1.0)) {
          P.aimed(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: tri(boss.phaseTime, 6, 4, 7), speed: 3.0, r: 3, color: 'orb_purple', jitter: 0.06 });
        }
        // 凝视锁定：弹悬停 0.7s（眼睛在看你）后朝自机当下位置突进（与机械链弹区分：锁目标）
        if (fireTimer(boss, dt, 1.8, '_ft2')) {
          const ks = ctx.diffIdx >= 2 ? [-2, -1, 0, 1, 2] : [-1, 0, 1]; // Hard+ 加两路凝视
          for (const k of ks) {
            const a = aim + k * 0.13;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 2.2, angle: a, r: 3, color: 'orb_blue', onUpdate: lockonBullet(0.7, 3.4) });
          }
        }
      },
    },
    {
      name: '暗符「无光之海」', spellCard: true,
      desc: "海没有光，一波一波地涨。你在里面能看见什么？什么都看不见，除了我在看你。", hp: 1000, timeLimit: 50,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 70, 110, 8);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 签名：环弹 12→20 涨落（海潮）+ 环来回扫——全场挂 shadowBullet：无光的海，
        // 弹只在自机 200px 内显形（难度从弹量转移到能见度管理）
        if (fireTimer(boss, dt, 1.0)) {
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: tri(boss.phaseTime, 8, 12, 20), speed: 1.5, r: 3, offset: sweepAngle(boss.phaseTime, 0.3, 5), color: 'orb_purple', onUpdate: shadowBullet(200, 0.18) });
        }
        if (fireTimer(boss, dt, 2.5, '_ft2')) {
          P.homing(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 3, speed: 1.7, homing: 0.6, r: 4, color: 'drop_blue', onUpdate: composeBullets(guideBullet(6), shadowBullet(200, 0.18)) });
        }
      },
    },
    {
      name: '加速突袭', spellCard: false, hp: 550, timeLimit: 28,
      enter: enterAt(240, 120),
      update(boss, dt, ctx) {
        const tx = 240 + Math.sin(boss.phaseTime * 0.8) * 120;
        bossMove(boss, dt, tx, boss._ty, 120, 0, 0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 签名：突袭间隔 0.7→1.1 急缓交替 + 扇面弹数按难度爬坡（修复：原写死 5 颗不吃缩放，四难度全 15）
        if (fireTimer(boss, dt, osc(boss.phaseTime, 6, 0.7, 1.1))) {
          const volley = P.diffCount(5);
          for (let i = 0; i < volley; i++) {
            const a = aim + (i - (volley - 1) / 2) * 0.12;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 1.8, angle: a, r: 3, color: 'pill_pink', onUpdate: accelBullet(1.1) });
          }
        }
      },
    },
    {
      name: '眼符「虚空凝视」', spellCard: true,
      desc: "被盯着的东西会越转越快。我的视线不拐弯，我的弹幕会。眨眼，就算你输。", hp: 1100, timeLimit: 55,
      enter: enterAt(240, 150),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 80, 80, 0, 1.0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 虚空凝视：旋臂凝视 + 加速暗弹（与虚无降临的旋臂环海区分）
        // 签名：旋臂每 5s 换向 + 加速率 0.8→1.3 渐强
        if (fireTimer(boss, dt, 0.15)) {
          P.spiral(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { arms: 4, spin: 2.8 * flip(boss.phaseTime, 5), speed: 2.0, r: 3, color: 'orb_purple' });
        }
        if (fireTimer(boss, dt, 1.2, '_ft2')) {
          // 瞬转再瞄组：每 0.9s 瞬时重瞄一次、共 3 次、每瞄提速 1.05（"一瞥一瞥"的凝视节奏，
          // EoSD 式离散追踪——与加速弹的"冲"区分）
          for (const k of [-1, 0, 1]) {
            const a = aim + k * 0.14;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 2.2, angle: a, r: 3, color: 'pill_pink', onUpdate: snapBullet(0.9, 3, 1.05) });
          }
        }
      },
    },
    {
      name: '瞳孔收缩', spellCard: false, hp: 600, timeLimit: 30,
      enter: enterAt(240, 130),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 120, 140, 0, 1.5);
        const aim = aimOf(ctx, boss.x, boss.y);
        if (fireTimer(boss, dt, 0.9)) {
          P.aimed(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 7, speed: 3.5, spread: 0.09, r: 3, color: 'orb_purple' });
        }
        // 收缩摆动：蛇形收束弹（与凝视的加速暗弹区分）
        if (fireTimer(boss, dt, 2.0, '_ft2')) {
          const n = P.diffCount(7);
          for (let i = 0; i < n; i++) {
            const a = aim + (i - (n - 1) / 2) * 0.16;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 2.6, angle: a, r: 3, color: 'orb_blue', onUpdate: zigzagBullet(2.0, 6) });
          }
        }
      },
    },
    {
      name: '虚符「虚无降临」', spellCard: true,
      desc: "紫色，蓝色，紫色，蓝色。虚无没有颜色，所以什么颜色都可以是。沉进去吧。", hp: 1200, timeLimit: 60,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 70, 100, 10, 0.8);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 签名：主色紫/蓝每 10s 轮换（虚无变色）+ 旋速 -2.4→-3.4 呼吸
        if (fireTimer(boss, dt, 0.14)) {
          P.spiral(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { arms: 5, spin: osc(boss.phaseTime, 12, -2.4, -3.4), speed: 2.2, r: 3, color: seq(boss.phaseTime, 10, ['orb_purple', 'orb_blue']) });
        }
        if (fireTimer(boss, dt, 1.5, '_ft2')) {
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 20, speed: 1.8, r: 3, offset: boss.phaseTime * 0.7, color: 'orb_blue' });
        }
        // 夜幕显形雨：慢速暗雨落下时几乎隐形，进入自机 220px 才现形（夜幕低垂，
        // 与 ghost 的鬼影幢幢同原语不同语境；撤掉复制粘贴的加速三连/Hard 追踪）
        if (fireTimer(boss, dt, 2.2, '_ft3')) {
          P.rain(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 5, speedMin: 1.2, speedMax: 2.0, drift: 0.25, r: 3, color: 'orb_purple', onUpdate: shadowBullet(220) });
        }
      },
    },
    {
      name: '暗雨', spellCard: false, hp: 600, timeLimit: 30,
      enter: enterAt(240, 120),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 110, 130, 0, 1.3);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 暗雨：高速暗色落雨 + 加速暗弹（与瞳孔收缩的收束连狙区分）
        if (fireTimer(boss, dt, 0.8)) {
          P.rain(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 8, speedMin: 2.4, speedMax: 3.8, drift: 0.3, r: 3, color: 'orb_purple' });
        }
        if (fireTimer(boss, dt, 1.6, '_ft2')) {
          for (const k of [-1, 0, 1]) {
            const a = aim + k * 0.14;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 1.8, angle: a, r: 3, color: 'pill_pink', onUpdate: accelBullet(1.2) });
          }
        }
      },
    },
    {
      name: '灭符「永夜降临」', spellCard: true,
      desc: "夜落下来的时候，连弹幕都会被压弯。换个方向，再压一次。永夜的意思就是：不会天亮。", hp: 1100, timeLimit: 55,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 80, 90, 10, 0.8);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 永夜降临：暗弹环被夜幕卷弯（曲线环）+ 加速狙（与虚无降临的反向旋臂区分）
        // 签名：卷曲方向每 5s 反转（夜幕换向）+ 发射间隔呼吸
        if (fireTimer(boss, dt, osc(boss.phaseTime, 5, 0.24, 0.36))) {
          const volley = P.diffCount(6);
          const rate = 0.9 * flip(boss.phaseTime, 5);
          for (let i = 0; i < volley; i++) {
            const a = boss.phaseTime * 1.3 + (i / volley) * TAU;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 1.8, angle: a, r: 3, color: 'orb_purple', onUpdate: curveBullet(rate) });
          }
        }
        if (fireTimer(boss, dt, 1.2, '_ft2')) {
          for (const k of [-2, -1, 0, 1, 2]) {
            const a = aim + k * 0.13;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 1.9, angle: a, r: 3, color: 'pill_pink', onUpdate: accelBullet(1.0) });
          }
        }
        // 无害夜幕影雨：暗滴远处隐形、近身显形（"永夜"的氛围本体，装饰层）
        if (fireTimer(boss, dt, 1.0, '_ft3')) {
          P.rain(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 6, speedMin: 1.0, speedMax: 1.8, drift: 0.2, r: 3, color: 'orb_blue', harmless: true, ttl: 8, onUpdate: shadowBullet(210, 0.12) });
        }
      },
    },
  ]);
}

// ============================== STAGE 6 星界·终焉 ==============================
// 主题弹幕：综合高密度 —— 弹幕变换（zigzag/split/accel 混合）+ 阶段演出。
// 符卡同屏弹量预算 ≤450（ring n=24~28 + spiral arms 4~6 组合）。

/** 中 boss：彗星骑士（4 阶段） */
export function s6Mid() {
  return Object.assign(bossDef('彗星骑士', '星界的先锋', 's6_mid_starspawn', [
    {
      name: '彗星冲锋', spellCard: false, hp: 600, timeLimit: 30,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        const tx = 240 + Math.sin(boss.phaseTime * 0.9) * 110;
        bossMove(boss, dt, tx, boss._ty, 110, 0, 0);
        const aim = aimOf(ctx, boss.x, boss.y);
        if (fireTimer(boss, dt, 0.9)) {
          P.aimed(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 6, speed: 3.2, spread: 0.11, color: 'star_yellow' });
        }
        // 彗星分裂：冲锋母弹定时分裂（与星尘尾迹的旋臂区分）
        if (fireTimer(boss, dt, 1.4, '_ft2')) {
          splitMotherVolley(ctx, boss.x, boss.y, aim, { count: 2, fan: 0.35, color: 'star_purple' });
        }
      },
    },
    {
      name: '彗符「星尘尾迹」', spellCard: true,
      desc: "彗星过境，尾迹会留下来陪你玩。小心，它们还会在半空碎成星尘——优雅，但是危险。", hp: 950, timeLimit: 48,
      enter: enterAt(240, 150),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 90, 110, 0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 星尘尾迹：旋臂拖尾 + 尾迹母弹分裂（与陨星碎击的分裂狙区分：旋臂主轴）
        // 签名：旋臂每 4s 换向 + 分裂时机 0.8/1.2s 交替
        if (fireTimer(boss, dt, 0.14)) {
          P.spiral(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { arms: 3, spin: 3.0 * flip(boss.phaseTime, 4), speed: 2.2, r: 4, color: 'star_purple' });
        }
        if (fireTimer(boss, dt, 2.0, '_ft2')) {
          splitMotherVolley(ctx, boss.x, boss.y, aim, { count: 3, fan: 0.5, atAge: seq(boss.phaseTime, 4, [0.8, 1.2]) });
        }
      },
    },
    {
      name: '骑士连击', spellCard: false, hp: 750, timeLimit: 32, dpsCap: 60, // 慢成型钳顶：3.5 套可见
      enter: enterAt(240, 130),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 110, 130, 0, 1.3);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 骑士连击：4.5s 序列三段击——一段扇形刺（逼走位）→ 二段高速贯（抓走位）→
        // 三段分裂母弹终结（区域封锁）。段间隔 1.5s 即"读招"窗口（撤掉 plain homing）。
        const cyc = boss.phaseTime % 3.6;
        const seg = Math.floor(cyc / 1.2);
        if (boss._seg !== seg) {
          boss._seg = seg;
          if (seg === 0) {
            P.spread(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 9, arc: 0.8, speed: 2.6, color: 'star_cyan' });
          } else if (seg === 1) {
            P.aimed(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 5, speed: 3.4, color: 'star_yellow' });
          } else {
            splitMotherVolley(ctx, boss.x, boss.y, aim, { count: ctx.diffIdx >= 2 ? 3 : 2, fan: 0.4 });
          }
        }
      },
    },
    {
      name: '陨符「陨星碎击」', spellCard: true,
      desc: "正面接下我的陨星雨吧！砸下来还不算完，碎了还要再追你一段。骑士的问候，从来不只一下。", hp: 950, timeLimit: 48,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 100, 120, 0, 1.1);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 陨星碎击：斜掠流星雨（统一 lean ±0.35 逐波换向 + 彗尾）+ 定时分裂的母弹
        // 签名：掠向逐波交替 + 陨雨极速 3.0→4.2 增强 + 分裂张角 0.4/0.6 交替
        if (fireTimer(boss, dt, 0.9)) {
          const lean = seq(boss.phaseTime, 4, [0.24, -0.24, 0.3, -0.18]); // 整波统一掠向（辐射点；幅度逐波变化）
          // 屏外发射全屏覆盖（设计决策：掠向只作质感，不给免费安全区；grace 4s 豁免入场）
          const xMin = 16 + lean * 660, xMax = 464 + lean * 660; // 生成窗随掠向带符号平移（落点覆盖全屏）
          P.rain(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 8, speedMin: 2.2, speedMax: osc(boss.phaseTime, 6, 3.0, 4.2), drift: 0.15, lean, color: 'star_yellow', xMin, xMax, grace: 4, onUpdate: trailBullet(ctx.field) });
        }
        if (fireTimer(boss, dt, 2.4, '_ft2')) {
          splitMotherVolley(ctx, boss.x, boss.y, aim, { count: 3, fan: 0.5, color: 'star_purple', spread: seq(boss.phaseTime, 4.8, [0.4, 0.6]) });
        }
      },
    },
  ]), { mid: true });
}

/** 大 boss：星尘核心（8 阶段，最终） */
export function s6Big() {
  return bossDef('星尘核心', '终焉之核', 's6_boss_starcore', [
    {
      name: '星尘散弹', spellCard: false, hp: 600, timeLimit: 30,
      enter: enterAt(240, 130),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 90, 100, 0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 星尘散弹：星尘花绽 + 自机狙（与弹幕变换的变色环区分）
        if (fireTimer(boss, dt, 1.4)) {
          P.flower(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 14, petals: 5, speed: 2.0, amp: 0.8, color: 'star_yellow' });
        }
        if (fireTimer(boss, dt, 1.2, '_ft2')) {
          P.aimed(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 5, speed: 3.2, color: 'orb_red' });
        }
      },
    },
    {
      name: '星符「银河回旋」', spellCard: true,
      desc: "银河就这样转了一百亿年，快一阵，慢一阵。你倒是第一个想从旋臂里穿过去的。", hp: 1000, timeLimit: 50,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 80, 90, 0, 0.9);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 签名：旋速 2.8→3.8 呼吸 + 花瓣 5/6 轮换 + 双层反向旋臂（外正内反的银河绞盘，评审 Q2 双层结构）
        if (fireTimer(boss, dt, 0.12)) {
          P.spiral(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { arms: 5, spin: osc(boss.phaseTime, 9, 2.8, 3.8), speed: 2.2, r: 4, color: 'star_cyan' });
        }
        if (fireTimer(boss, dt, 0.21, '_ft0')) {
          P.spiral(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { arms: 3, spin: -osc(boss.phaseTime, 9, 2.8, 3.8) * 0.7, speed: 1.6, r: 4, color: 'star_purple' });
        }
        if (fireTimer(boss, dt, 2.0, '_ft2')) {
          P.flower(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 16, petals: seq(boss.phaseTime, 8, [5, 6]), speed: 2.0, amp: 0.8, color: 'star_purple' });
        }
      },
    },
    {
      name: '弹幕变换', spellCard: false, hp: 550, timeLimit: 28,
      enter: enterAt(240, 120),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 100, 120, 0, 1.2);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 交替 ring（变色变向）+ 蛇形扇 + 分裂弹
        if (fireTimer(boss, dt, 0.9)) {
          const flip = Math.floor(boss.phaseTime / 0.9) % 2 === 0;
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, {
            n: 18, speed: 2.3, offset: flip ? boss.phaseTime : -boss.phaseTime,
            color: flip ? 'star_pink' : 'star_cyan',
          });
        }
        if (fireTimer(boss, dt, 1.3, '_ft2')) {
          const volley = P.diffCount(7);
          for (let i = 0; i < volley; i++) {
            const a = aim + (i - (volley - 1) / 2) * 0.2;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 2.5, angle: a, r: 4, color: 'pill_pink', onUpdate: zigzagBullet(2.2, 4) });
          }
        }
        if (fireTimer(boss, dt, 2.2, '_ft3')) {
          splitMotherVolley(ctx, boss.x, boss.y, aim, { count: 3, fan: 0.45, speed: 2.0, chain: 0.3 }); // V5 链式逐裂（引线感，"变换"主题）
        }
      },
    },
    {
      name: '核符「超新星爆发」', spellCard: true,
      desc: "恒星燃尽的时候会换一种颜色，再换一种颜色。看好了——这是我最亮的样子。", hp: 1100, timeLimit: 55,
      enter: enterAt(240, 150),
      update(boss, dt, ctx) {
        // 阶段演出：缓慢逼近版中，密度拉满（同屏 ~400）
        bossMove(boss, dt, boss._tx, boss._ty, 60, 60, 10, 0.7);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 签名：大环三色轮换（超新星变色）+ 爆发壳脉冲（每 3.2s 一层快亮壳冲出辐射场）
        if (fireTimer(boss, dt, 0.09)) {
          P.spiral(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { arms: 6, spin: 2.6, speed: 2.4, r: 4, color: 'orb_red' });
        }
        if (fireTimer(boss, dt, 1.1, '_ft2')) {
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 32, speed: 2.0, offset: boss.phaseTime * 0.8, color: seq(boss.phaseTime, 5, ['star_purple', 'orb_red', 'star_cyan']) });
        }
        // 爆发壳：快速亮壳（速度差 = 冲击波可读性；密→嘭→密的呼吸）
        if (fireTimer(boss, dt, 3.2, '_ft3')) {
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 16, speed: 3.6, offset: boss.phaseTime, color: 'star_yellow' });
        }
      },
    },
    {
      name: '终焉流星雨', spellCard: false, hp: 600, timeLimit: 30,
      enter: enterAt(240, 120),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 110, 150, 0, 1.4);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 签名：雨势极速 3.8→4.8 起伏 + 掠向 ±0.3 逐波换向（流星雨语言与陨星碎击统一）
        if (fireTimer(boss, dt, 0.6)) {
          const lean = seq(boss.phaseTime, 4, [0.26, -0.26, 0.2, -0.32]); // 掠向逐波变化
          const xMin = 16 + lean * 660, xMax = 464 + lean * 660; // 生成窗随掠向带符号平移（落点覆盖全屏）
          P.rain(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 9, speedMin: 2.5, speedMax: osc(boss.phaseTime, 5, 3.8, 4.8), drift: 0.2, lean, color: 'star_yellow', xMin, xMax, grace: 4, onUpdate: trailBullet(ctx.field, { step: 0.12, fade: 0.3 }) }); // 屏外发射全屏覆盖
        }
        // 大火流星：scale 1.8 慢降大流星，橙粗尾，2.2s 烧尽裂成 3 碎星
        //（composeBullets 首次复合两原语：trail+burst＝流星的一生；分裂主题的第二种表情）
        if (fireTimer(boss, dt, 3.5, '_ft3')) {
          ctx.field.spawn({
            x: randRange(60, 420), y: -8, speed: 1.6, angle: Math.PI / 2, r: 7,
            color: 'star_yellow', scale: 1.8,
            onUpdate: composeBullets(
              trailBullet(ctx.field, { step: 0.06, fade: 0.5, r: 3, color: 'star_orange' }),
              burstBullet(ctx.field, 2.2, { count: 3, spread: 1.0, speed: 2.4, r: 4, color: 'star_yellow' }),
            ),
          });
        }
      },
    },
    {
      name: '星符「宇宙收缩」', spellCard: true,
      desc: "宇宙到最后会往回收。反着转，缩着走，所有花样一起来。能见证这一幕，你运气不错。", hp: 1200, timeLimit: 60,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        // 最终符：反向螺旋 + 大环 + 加速狙 + 定时链弹，弹幕变换总集
        // 签名：大环弹速 1.8→2.4 收缩感 + 旋臂每 6s 换向
        bossMove(boss, dt, boss._tx, boss._ty, 80, 80, 8, 0.8);
        const aim = aimOf(ctx, boss.x, boss.y);
        if (fireTimer(boss, dt, 0.09)) {
          P.spiral(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { arms: 4, spin: -3.2 * flip(boss.phaseTime, 6), speed: 2.6, r: 4, color: 'star_purple' });
        }
        if (fireTimer(boss, dt, 1.1, '_ft2')) {
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 36, speed: osc(boss.phaseTime, 7, 1.8, 2.4), offset: boss.phaseTime, color: 'star_cyan' });
        }
        if (fireTimer(boss, dt, 0.8, '_ft3')) {
          for (const k of [-1, 0, 1]) {
            const a = aim + k * 0.13;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 2.0, angle: a, r: 4, color: 'orb_red', onUpdate: accelBullet(0.8) });
          }
        }
        if (fireTimer(boss, dt, 2.6, '_ft4')) {
          chainVolley(ctx.field, boss.x, boss.y, aim, { n: 5, arc: 0.8, speed: 3.2, color: 'star_cyan' });
        }
        // Hard+ 追加组件：母弹分裂狙
        if (ctx.diffIdx >= 2 && fireTimer(boss, dt, 3.0, '_ft5')) {
          splitMotherVolley(ctx, boss.x, boss.y, aim, { count: 2, fan: 0.4, spread: 0.6 });
        }
      },
    },
    {
      name: '星尘壁垒', spellCard: false, hp: 600, timeLimit: 30,
      enter: enterAt(240, 130),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 100, 110, 0, 1.2);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 星尘壁垒：星屑弹幕墙 + 分裂母弹（与弹幕变换的变色环区分）
        if (fireTimer(boss, dt, 2.0)) {
          P.wall(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 22, speed: 1.9, gapIndex: (boss.phaseTime * 0.15) % 1, color: 'star_cyan' }); // 缺口连续滚动（星环轨道）
        }
        if (fireTimer(boss, dt, 2.4, '_ft2')) {
          splitMotherVolley(ctx, boss.x, boss.y, aim, { count: 2, fan: 0.35 });
        }
      },
    },
    {
      name: '煌符「星火燎原」', spellCard: true,
      desc: "一点星火，被热浪卷成环，碎开来就是一片原。燃烧这种事，我最懂了。", hp: 1100, timeLimit: 55,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 80, 90, 10, 0.9);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 星火燎原：环状星火被热浪卷弯（曲线环）+ 分裂母弹（与宇宙收缩的反向旋臂区分）
        // 签名：热浪卷曲率 -0.6→-1.2 呼吸 + 母弹双色交替
        if (fireTimer(boss, dt, 0.35)) {
          const volley = P.diffCount(6);
          const rate = osc(boss.phaseTime, 6, -0.6, -1.2);
          for (let i = 0; i < volley; i++) {
            const a = -boss.phaseTime * 1.6 + (i / volley) * TAU;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 2.0, angle: a, r: 4, color: 'orb_red', onUpdate: curveBullet(rate) });
          }
        }
        // 爆发壳：每 4s 一层金色快壳（超新星的壳层语言，"燎原"的脉冲）
        if (fireTimer(boss, dt, 4.0, '_ft3')) {
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 12, speed: 3.4, offset: boss.phaseTime, color: 'star_yellow' });
        }
        // 无害星火余烬：橙色余烬旋出（装饰层）
        if (fireTimer(boss, dt, 0.25, '_ft4')) {
          P.spiral(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { arms: 2, spin: -3.4, speed: 1.4, r: 2, color: 'star_orange', harmless: true });
        }
        if (fireTimer(boss, dt, 1.7, '_ft2')) {
          splitMotherVolley(ctx, boss.x, boss.y, aim, { count: 2, fan: 0.4, color: seq(boss.phaseTime, 4.8, ['star_pink', 'star_yellow']), childColor: 'star_yellow' });
        }
      },
    },
  ]);
}

// ============================== EXTRA 星灵·终焉回廊 ==============================
// Extra 模式 boss：10 张符卡连战（全 spellCard），机制总集篇，密度按 Hard 档设计。

/** Extra boss：星灵（10 阶段全符卡） */
export function exBig() {
  return bossDef('星灵', '终焉回廊的支配者', 'ex_boss', [
    {
      name: '壹符「星屑迎撃」', spellCard: true,
      desc: "回廊的规矩第一条：先接住星屑。旋臂转着，礼花开着——欢迎来到终焉回廊。", hp: 900, timeLimit: 45,
      enter: enterAt(240, 130),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 100, 110, 0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 星屑迎撃：旋臂扫场 + 拦截锁定组（悬停 0.8s 火控解算 → 朝自机当下位置拦截）
        // 签名：旋臂每 4s 换向 + 锁定拦截波（与 S5 凝视同原语——总集回收）
        if (fireTimer(boss, dt, 0.2)) {
          P.spiral(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { arms: 5, spin: 2.62 * flip(boss.phaseTime, 4), speed: 2.0, r: 4, color: 'star_yellow' }); // Hard 基准迁移（输出不变）
        }
        if (fireTimer(boss, dt, 1.9, '_ft2')) {
          const ks = [-4, -3, -2, -1, 0, 1, 2, 3, 4]; // 9 路拦截（Ex 固定）
          for (const k of ks) {
            const a = aim + k * 0.16;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 2.4, angle: a, r: 4, color: 'orb_red', onUpdate: lockonBullet(0.8, 3.6) });
          }
        }
      },
    },
    {
      name: '贰符「流星追尾」', spellCard: true,
      desc: "流星一旦认出你，就会一直追。追上之前，还会在半空开花。被星星喜欢，是要付出代价的。", hp: 950, timeLimit: 48,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 90, 100, 8);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 流星追尾：彗尾追尾流星（粉星尘尾把追踪轨迹画出来；燃尽点对齐追踪窗口+离场段 6.5s）+ 母弹分裂
        // 签名：追踪强度 0.5→0.9 锁定 + 分裂时机 0.8/1.2s 交替
        if (fireTimer(boss, dt, 1.5)) {
          P.homing(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 5, speed: 2.0, homing: osc(boss.phaseTime, 6, 0.55, 0.91), color: 'star_pink', onUpdate: composeBullets(guideBullet(5), trailBullet(ctx.field, { color: 'star_pink', maxAge: 6.5 })) });
        }
        if (fireTimer(boss, dt, 2.2, '_ft2')) {
          splitMotherVolley(ctx, boss.x, boss.y, aim, { count: 2, fan: 0.4, atAge: seq(boss.phaseTime, 4.4, [0.8, 1.2]), mode: 'burst', burstCount: 8, burstSpeed: 1.5 }); // V4 环裂开花（"星尘礼花"本体）
          // 装饰：母弹出膛时的星尘礼花（纯装饰 harmless，不当绒毛判负）
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 13, speed: 1.4, r: 3, offset: boss.phaseTime, color: 'star_pink', harmless: true });
        }
        // 装饰：流星尾迹的青色星尘花（纯装饰 harmless，衬托流星主题）
        if (fireTimer(boss, dt, 2.8, '_ft3')) {
          P.flower(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 16, petals: 5, speed: 1.1, amp: 0.5, r: 3, color: 'star_cyan', harmless: true });
        }
      },
    },
    {
      name: '叁符「十字激光阵」', spellCard: true,
      desc: "十字封场，来回地扫。回廊里没有死角，只有还没扫到的地方。", hp: 900, timeLimit: 45,
      enter: enterAt(240, 130),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 80, 90, 0);
        // 签名：十字束每 5s 换向（来回扫场）+ 扇形 5→8 爬坡
        if (fireTimer(boss, dt, 0.6)) {
          P.laserFan(ctx.field, boss.x, boss.y, boss.phaseTime, sweepAngle(boss.phaseTime, 1.1, 5), { n: 6.5, arc: 1.6, speed: 1.6, color: 'orb_red' });
        }
        const aim = aimOf(ctx, boss.x, boss.y);
        if (fireTimer(boss, dt, 2.4, '_ft2')) {
          P.spread(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: tri(boss.phaseTime, 6, 8, 13), arc: 0.9, speed: 2.6, color: 'star_cyan' });
        }
        // 装饰：激光交汇的能量火花（低威胁金色慢环）
        if (fireTimer(boss, dt, 2.5, '_ft3')) {
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 8, speed: 1.2, r: 3, offset: boss.phaseTime * 0.6, color: 'star_yellow' });
        }
      },
    },
    {
      name: '肆符「曲线回廊」', spellCard: true,
      desc: "直来直去的回廊多没意思。我把它卷起来——墙还是那堵墙，路可不是那条路了。", hp: 950, timeLimit: 48,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 70, 120, 10);
        // 签名：卷曲方向每 5s 反转 + 回廊墙缺口左-中-右轮换
        if (fireTimer(boss, dt, 0.4)) {
          const volley = P.diffCount(6.5);
          const rate = 0.9 * flip(boss.phaseTime, 5);
          for (let i = 0; i < volley; i++) {
            const a = boss.phaseTime * 1.2 + (i / volley) * TAU;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 1.7, angle: a, r: 5, color: 'star_purple', onUpdate: curveBullet(rate) });
          }
        }
        const aim = aimOf(ctx, boss.x, boss.y);
        // 曲线回廊：曲线环 + 回廊墙封位
        if (fireTimer(boss, dt, 2.6, '_ft2')) {
          P.wall(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 29, speed: 1.7, gapIndex: (boss.phaseTime * 0.1) % 1, color: 'star_purple' }); // 缺口连续巡游（回廊）
        }
      },
    },
    {
      name: '伍符「加速乱舞」', spellCard: true,
      desc: "舞会开始了。灯球转着，舞步越跳越快。踩错一拍？那就当给我助兴了。", hp: 900, timeLimit: 45,
      enter: enterAt(240, 120),
      update(boss, dt, ctx) {
        const tx = 240 + Math.sin(boss.phaseTime * 0.9) * 120;
        bossMove(boss, dt, tx, boss._ty, 120, 0, 0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 签名：加速率 0.8→1.3 乱舞 + 环弹来回扫
        if (fireTimer(boss, dt, 1.1)) {
          const volley = P.diffCount(10);
          const rate = osc(boss.phaseTime, 5, 0.8, 1.3);
          for (let i = 0; i < volley; i++) {
            const a = aim + (i - (volley - 1) / 2) * 0.16;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 1.8, angle: a, r: 4, color: 'pill_pink', onUpdate: accelBullet(rate) });
          }
        }
        if (fireTimer(boss, dt, 2.6, '_ft2')) {
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 23, speed: 1.4, offset: sweepAngle(boss.phaseTime, 0.8, 4), color: 'star_cyan' });
        }
        // 装饰：舞台灯光般的金色星尘花（低威胁，乱舞的舞台感）
        if (fireTimer(boss, dt, 3.0, '_ft3')) {
          P.flower(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 8, petals: 4, speed: 1.0, amp: 0.4, r: 3, color: 'star_yellow' });
        }
      },
    },
    {
      name: '陆符「菌毯蔓延」', spellCard: true,
      desc: "借来的地毯，意外地好用。孢子在回廊里扎了根，一圈一圈地等你。——谢啦，菇后。", hp: 1000, timeLimit: 50, dpsCap: 60, dpsCapFirst: [6, 30], // 慢成型钳顶 + 开场 6s 减伤（菌毯展开慢）
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 60, 80, 0);
        // 菌毯蔓延：版中菌环（同 S4 重做——(240,320) 的 fairy ring，涟漪预告后三环滞成毯、
        // 滞留后径向散射+下沉；旧版悬在 boss 身边 27/49/70px=无效威胁）
        if (fireTimer(boss, dt, 2.0)) {
          if (ctx.effects && ctx.effects.telegraphRing) ctx.effects.telegraphRing(240, 320, 180, '#c9f', 0.5);
          (boss._matQ = boss._matQ || []).push({ at: boss.phaseTime + 0.5, base: boss.phaseTime * 0.4 });
        }
        if (boss._matQ && boss._matQ.length && boss.phaseTime >= boss._matQ[0].at) {
          const q = boss._matQ.shift();
          for (const [r0, n] of [[60, P.holdRingCount(12, 5, 60)], [120, P.holdRingCount(24, 5, 120)], [180, P.holdRingCount(36, 5, 180)]]) {
            for (let i = 0; i < n; i++) {
              const a = q.base + (i / n) * TAU + (r0 % 180 ? TAU / (2 * n) : 0);
              ctx.field.spawn({
                x: 240 + Math.cos(a) * r0, y: 320 + Math.sin(a) * r0,
                speed: 0.5, angle: a, r: 5, color: 'orb_green', onUpdate: sporeBullet(1.6),
              });
            }
          }
        }
        // 装饰：孢子尘飘浮（纯装饰 harmless，菌环周围的孢子雾）
        if (fireTimer(boss, dt, 2.0, '_ft3')) {
          P.rain(ctx.field, boss.x, boss.y, boss.phaseTime, aimOf(ctx, boss.x, boss.y), { n: 10, speedMin: 0.6, speedMax: 1.0, drift: 0.4, r: 3, color: 'drop_green', harmless: true, ttl: 10 });
        }
      },
    },
    {
      name: '柒符「蛇形矩阵」', spellCard: true,
      desc: "蛇会走直线吗？矩阵会扭吗？在我这里都会。扭的幅度嘛，看我心情。", hp: 950, timeLimit: 48,
      enter: enterAt(240, 130),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 110, 130, 0, 1.2);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 签名：蛇形幅度 1.6→2.8 呼吸 + 矩阵墙缺口左-中-右轮换
        if (fireTimer(boss, dt, 1.2)) {
          const volley = P.diffCount(11.5);
          const amp = osc(boss.phaseTime, 5, 1.6, 2.8);
          for (let i = 0; i < volley; i++) {
            const a = aim + (i - (volley - 1) / 2) * 0.2;
            ctx.field.spawn({ x: boss.x, y: boss.y, speed: 2.4, angle: a, r: 4, color: 'pill_pink', onUpdate: zigzagBullet(amp, 4) });
          }
        }
        if (fireTimer(boss, dt, 2.4, '_ft2')) {
          P.wall(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 32, speed: 1.8, gapIndex: (Math.sin(boss.phaseTime * 0.4) + 1) / 2, color: 'star_purple' }); // 缺口连续扫描摆动
        }
      },
    },
    {
      name: '捌符「终焉·全弹发射」', spellCard: true,
      desc: "终焉第一幕：全弹发射。青的紫的一起来，转够八秒换一边。撑住，这才刚开始。", hp: 1200, timeLimit: 60,
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 80, 80, 8, 0.8);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 签名：旋臂每 8s 换向（段落演化）+ 环弹青/紫交替
        if (fireTimer(boss, dt, 0.14)) {
          P.spiral(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { arms: 6.5, spin: 3.27 * flip(boss.phaseTime, 8), speed: 2.2, r: 4, color: 'star_purple' });
        }
        if (fireTimer(boss, dt, 1.4, '_ft2')) {
          P.ring(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 29, speed: 1.9, offset: boss.phaseTime * 0.7, color: seq(boss.phaseTime, 6, ['star_cyan', 'star_purple']) });
        }
        if (fireTimer(boss, dt, 2.2, '_ft3')) {
          P.flower(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 19.5, petals: 5, speed: 1.9, amp: 0.8, color: 'star_pink' });
        }
        // Hard+ 追加组件：分裂母弹（Extra 恒按 Hard 档）
        if (ctx.diffIdx >= 2 && fireTimer(boss, dt, 3.0, '_ft4')) {
          ctx.field.spawn({
            x: boss.x, y: boss.y, speed: 2.2, angle: aim, r: 5, color: 'star_yellow',
            onUpdate: splitBullet(ctx.field, 1.0, 0.6, 'star_pink'),
          });
        }
      },
    },
    {
      name: '玖符「星链锁阵」', spellCard: true,
      desc: "星链一扣一扣地锁，墙一排一排地围。咔、咔——停。你猜，下一扣锁在哪里？", hp: 1000, timeLimit: 50,
      enter: enterAt(240, 130),
      update(boss, dt, ctx) {
        bossMove(boss, dt, boss._tx, boss._ty, 90, 100, 0, 1.0);
        const aim = aimOf(ctx, boss.x, boss.y);
        // 星链锁阵：鞭链——10 颗链节 0.07s 密排同向连发（青/金交替），看得见的链段鞭向自机
        //（空间轴的"链"，与 S3 定时链弹"时间轴的链"区分）+ 锁阵墙
        if (burstTimer(boss, dt, 0.07, 20, 2.2)) { // 鞭链 20 节（Ex 固定）
          boss._link = (boss._link || 0) + 1;
          ctx.field.spawn({ x: boss.x, y: boss.y, speed: 3.0, angle: aim, r: 4, color: boss._link % 2 === 0 ? 'star_cyan' : 'star_yellow' });
        }
        if (fireTimer(boss, dt, 2.6, '_ft2')) {
          P.wall(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 39, speed: 1.8, gapIndex: (Math.sin(boss.phaseTime * 0.6) + 1) / 2, color: 'star_purple' }); // 缺口连续快速扫描（锁定）
        }
      },
    },
    {
      name: '拾符「终焉·星灵全开」', spellCard: true,
      desc: "最后一幕，星灵全开。花按彩虹的顺序开，蛇形按着呼吸扭。过了这关，回廊就没有秘密了。", hp: 1300, timeLimit: 60, dpsCap: 40, // 慢成型钳顶：10.8s/幕
      enter: enterAt(240, 140),
      update(boss, dt, ctx) {
        // 最终符：三幕巡礼（HP 分段门控为主、时间轴兜底——CAVE 阈值模型）：
        // 血量 >2/3 一幕「灯火·满潮」（亮追/灭咬）→ 1/3-2/3 二幕「漩涡·噬流」（收口咬人）→ <1/3 三幕「流星·火雨倾盆」（雨势爬坡+大火流星）；
        // 打得快按血量推进（任何 DPS 三幕必出场），打得慢按 36s 时间轴照常巡礼。
        bossMove(boss, dt, boss._tx, boss._ty, 70, 90, 10, 0.8);
        const aim = aimOf(ctx, boss.x, boss.y);
        const frac = boss.maxHp ? boss.hp / boss.maxHp : 1;
        const actHp = frac > 2 / 3 ? 0 : frac > 1 / 3 ? 1 : 2;
        const act = Math.max(actHp, Math.floor((boss.phaseTime % 36) / 12));
        if (act !== 1 && boss._whirlQ) boss._whirlQ.length = 0; // 幕切走即丢弃未成熟队列：回访时不许无预告出弹（防初见杀契约）
        if (act === 0) {
          // 一幕「灯火·满潮」：亮时群灯追猎（同相明灭），灭时灯灭则咬（lockon×3 突袭）——
          // 亮追/灭咬的交替结构（灯笼鱼捕食的完整句）
          if (fireTimer(boss, dt, 1.2, '_a0')) {
            P.homing(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 16, speed: 1.9, homing: 0.87, r: 5, color: 'orb_yellow', onUpdate: composeBullets(guideBullet(6), flickerBullet(3.2, 0.15, false, () => boss.phaseTime)) });
          // 无害星尘雨（灯火满潮的氛围底层）
          if (fireTimer(boss, dt, 1.4, '_a0c')) {
            P.rain(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 6.5, speedMin: 0.8, speedMax: 1.4, drift: 0.3, r: 2, color: 'star_yellow', harmless: true, ttl: 8 });
          }
          }
          const flickPh = (Math.sin((boss.phaseTime / 3.2) * TAU - Math.PI / 2) + 1) / 2; // 与灯火同相的灭相窗口
          if (flickPh < 0.25 && fireTimer(boss, dt, 0.6, '_a0b')) {
            for (const k of [-2, -1, 0, 1, 2]) {
              const a = aim + k * 0.15;
              ctx.field.spawn({ x: boss.x, y: boss.y, speed: 2.2, angle: a, r: 4, color: 'orb_red', onUpdate: lockonBullet(0.6, 3.6) });
            }
          }
        } else if (act === 1) {
          // 二幕「漩涡·噬流」：收缩环（涟漪预告 0.6s）+ 排干瞬间每 2 颗改咬自机
          if (fireTimer(boss, dt, 1.0, '_a1')) {
            if (ctx.effects && ctx.effects.telegraphRing) ctx.effects.telegraphRing(240, 300, 200, '#c9f', 0.6);
            (boss._whirlQ = boss._whirlQ || []).push({ at: boss.phaseTime + 0.6, base: boss.phaseTime * 0.7 });
          }
          if (boss._whirlQ && boss._whirlQ.length && boss.phaseTime >= boss._whirlQ[0].at) {
            const q = boss._whirlQ.shift();
            const n = P.diffCount(27.6);
            for (let i = 0; i < n; i++) {
              const th0 = q.base + (i / n) * TAU;
              ctx.field.spawn({
                x: 240 + Math.cos(th0) * 200, y: 300 + Math.sin(th0) * 200,
                speed: 0, angle: 0, r: 5, color: 'star_purple',
                onUpdate: whirlpoolBullet(240, 300, { r0: 200, r1: 30, dur: 3.0, omega: 2.4, theta0: th0, bite: 2 }),
              });
            }
          }
        } else {
          // 三幕「流星·火雨倾盆」：彗尾雨 n:12 + 间隔随幕内时间 0.9→0.65 爬坡 + 大火流星间奏
          if (boss._act2Start === undefined) boss._act2Start = boss.phaseTime;
          const actT = boss.phaseTime - boss._act2Start;
          if (fireTimer(boss, dt, Math.max(0.75, 0.9 - actT * 0.012), '_a2')) {
            const lean = seq(boss.phaseTime, 4, [0.24, -0.24, 0.3, -0.18]); // 掠向逐波变化
            const xMin = 16 + lean * 660, xMax = 464 + lean * 660; // 生成窗随掠向带符号平移（落点覆盖全屏）
            P.rain(ctx.field, boss.x, boss.y, boss.phaseTime, aim, { n: 16, speedMin: 2.2, speedMax: 3.4, drift: 0.15, lean, color: 'star_yellow', xMin, xMax, grace: 4, onUpdate: trailBullet(ctx.field, { step: 0.12 }) }); // 屏外发射全屏覆盖
          }
          if (fireTimer(boss, dt, 2.8, '_a2b')) {
            ctx.field.spawn({
              x: randRange(60, 420), y: -8, speed: 1.6, angle: Math.PI / 2, r: 7,
              color: 'star_yellow', scale: 1.8,
              onUpdate: composeBullets(
                trailBullet(ctx.field, { step: 0.06, fade: 0.5, r: 3, color: 'star_orange' }),
                burstBullet(ctx.field, 2.2, { count: 3, spread: 1.0, speed: 2.4, r: 4, color: 'star_yellow' }),
              ),
            });
          }
        }
      },
    },
  ]);
}
